// API Detail page with Try It
import React, { useState as udS, useMemo as udM } from 'react';
import { createPortal } from 'react-dom';
import { XPrim, XIcons } from '../primitives.jsx';
import { XData, XEnv } from '../data.jsx';
import { XApi } from '../services.js';
import { XShell } from '../shell.jsx';
import { useT } from '../i18n.jsx';
import { Dialog, DialogHeader, DialogBody } from '../ui/index.js';
import { renderSkillMarkdown } from '../skills/markdown.js';
import {
  ROOTLABEL,
  deserializeRules,
  numericPreview,
  previewStr,
  ruleLabel,
} from './dynamic-rules.js';
import WsTryItPanel from './ws/ws-tryit-panel.jsx';
import { endpointListCost, isFreeEndpoint } from '../endpoint-pricing.js';
import { FreeApiAccessNotice, isFreeApiAccountError } from '../components/FreeApiAccessNotice.jsx';
import { ModelReferencePrices, hasModelReferenceQuotes } from '../components/ModelReferencePrices.jsx';
import {
  isOpenAiLiveEndpoint,
  openAiLiveDelegationPricingRows,
  resolveOpenAiLiveAuthorizationDisplay,
} from './ws/openai-live-pricing.js';

const safeWebsiteHref = (url) => {
  const s = String(url || '').trim();
  if (/^https?:\/\//i.test(s)) return s;
  return '';
};

// Guess mime from URL extension when the upstream doesn't provide mime_type.
// V2 nested file results (UPSTREAM_POLLING — see docs/异步任务/前端对接.md §3.1)
// come back as `body.type='file'` with a `body.url` but no mime, so we have to
// infer from the URL.
const EXT_MIME_MAP = {
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', m4v: 'video/x-m4v',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
  webp: 'image/webp', avif: 'image/avif', svg: 'image/svg+xml',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', flac: 'audio/flac',
};
const inferMimeFromUrl = (url) => {
  try {
    const u = new URL(url);
    const m = u.pathname.toLowerCase().match(/\.(\w+)(?:$)/);
    return m ? (EXT_MIME_MAP[m[1]] || '') : '';
  } catch { return ''; }
};

// Inline file renderer: handles image/* + video/* + audio/* preview from a signed URL,
// with a download fallback for other types. Shared between top-level R2 results (V1
// large response stored in object storage) and V2 nested results (upstream-provided
// video URL embedded in the JSON body).
// Human-friendly byte size. Previous version always divided by 1024 once and
// just swapped the suffix, so 1.4MB read as "1433.6 MB". Now divides twice
// when crossing the MB threshold.
const formatBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const TaskFileRenderer = ({ url, mime, bytes, expired }) => {
  const effectiveMime = (mime || inferMimeFromUrl(url) || '').toLowerCase();
  const isImage = effectiveMime.startsWith('image/');
  const isVideo = effectiveMime.startsWith('video/');
  const isAudio = effectiveMime.startsWith('audio/');
  // JSON-typed file (very common for AI image-gen async tasks): the upstream
  // response is multi-MB JSON with b64_json image data, so the xapi backend
  // overflows to R2 instead of inlining. We have to fetch the file and run
  // the same b64_json extractor as the inline branch so users see images
  // rather than a bare "Download" button.
  const isJson = effectiveMime.startsWith('application/json') || effectiveMime.startsWith('text/json');

  const [jsonImages, setJsonImages] = React.useState(null); // null = not loaded; [] = loaded none
  const [jsonLoading, setJsonLoading] = React.useState(false);
  const [jsonErr, setJsonErr] = React.useState(null);
  React.useEffect(() => {
    if (!isJson || !url || expired) return;
    let cancelled = false;
    setJsonLoading(true); setJsonErr(null);
    fetch(url)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((body) => { if (!cancelled) setJsonImages(extractB64Images(body)); })
      .catch((e) => { if (!cancelled) setJsonErr(e.message || 'Fetch failed'); })
      .finally(() => { if (!cancelled) setJsonLoading(false); });
    return () => { cancelled = true; };
  }, [isJson, url, expired]);

  if (expired) {
    return (
      <div className="row gap-2" style={{ padding: 12, background: 'var(--bg-2)', border: '1px solid var(--line)', borderRadius: 6, color: 'var(--fg-3)', fontSize: 13 }}>
        <XIcons.IconInfo size={13} />
        <span>File no longer available {bytes != null && `· ${(bytes || 0).toLocaleString()} bytes`}</span>
      </div>
    );
  }
  const sizeLabel = formatBytes(bytes);
  return (
    <div className="col gap-2">
      {isImage && url && (
        <a href={url} target="_blank" rel="noreferrer" style={{ display: 'block', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--line)', background: 'var(--bg-2)' }}>
          <img src={url} alt="Task result" loading="lazy" style={{ width: '100%', maxHeight: 480, objectFit: 'contain', display: 'block' }} />
        </a>
      )}
      {isVideo && url && (
        <video src={url} controls style={{ width: '100%', maxHeight: 480, borderRadius: 6, background: 'var(--bg-2)' }} />
      )}
      {isAudio && url && (
        <audio src={url} controls style={{ width: '100%' }} />
      )}
      {/* JSON file: fetched on mount, b64_json hits rendered as thumbnails. */}
      {isJson && jsonLoading && (
        <div className="row gap-2" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
          <div className="spinner" style={{ width: 12, height: 12 }} /> Loading result…
        </div>
      )}
      {isJson && jsonErr && (
        <div style={{ fontSize: 12, color: 'var(--fg-4)' }}>Couldn't fetch the JSON for inline preview ({jsonErr}). Use Download below.</div>
      )}
      {isJson && jsonImages && <B64ImageStrip images={jsonImages}/>}
      <div className="row gap-2 between" style={{ fontSize: 12, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
        <span className="mono">{effectiveMime || 'application/octet-stream'} {sizeLabel && `· ${sizeLabel}`}</span>
        {url && (
          <a className="btn btn-sm" href={url} target="_blank" rel="noreferrer" download>
            <XIcons.IconDownload size={11} /> Download
          </a>
        )}
      </div>
    </div>
  );
};

// Render an async task result. Two file shapes are recognized:
//   1) result.type === 'file'                          — V1 / R2-stored result (large bodies)
//   2) result.type === 'json' && result.body.type === 'file' && result.body.url
//      — V2 upstream-polling result where the upstream provider (e.g. BytePlus)
//        returns the asset URL inline as JSON. The xapi backend keeps it as JSON
//        because video URLs are small (<32 KB), but the *business* type is a file.
// Walks any value looking for image generation results keyed as `b64_json`
// (OpenAI / DALL·E / many image-gen APIs). Returns at most `max` hits to
// guard against pathological payloads. Each hit also captures `revised_prompt`
// if present in the same parent (so we can label the image).
const extractB64Images = (value, max = 20) => {
  const out = [];
  const walk = (v) => {
    if (out.length >= max) return;
    if (!v) return;
    if (Array.isArray(v)) { for (const x of v) walk(x); return; }
    if (typeof v !== 'object') return;
    if (typeof v.b64_json === 'string' && v.b64_json.length > 16) {
      out.push({
        b64: v.b64_json,
        revisedPrompt: typeof v.revised_prompt === 'string' ? v.revised_prompt : null,
      });
      if (out.length >= max) return;
    }
    for (const k of Object.keys(v)) {
      // Skip the b64_json field itself — we already captured it; walking into
      // a huge base64 string as if it were a sub-object is just wasted work.
      if (k === 'b64_json') continue;
      walk(v[k]);
    }
  };
  walk(value);
  return out;
};

// Sniff MIME from base64 magic prefix. Falls back to PNG which renders fine
// for unknown types in most browsers. Saves a network sniff and keeps the
// component a pure data → JSX transform.
const guessImageMime = (b64) => {
  if (!b64 || typeof b64 !== 'string') return 'image/png';
  const head = b64.slice(0, 8);
  if (head.startsWith('iVBOR')) return 'image/png';
  if (head.startsWith('/9j/')) return 'image/jpeg';
  if (head.startsWith('R0lG')) return 'image/gif';
  if (head.startsWith('UklGR')) return 'image/webp';
  return 'image/png';
};

// Renders any b64_json hits as a responsive thumbnail grid above the raw JSON.
// Click → opens fullscreen lightbox via XPrim.JsonModal-style overlay (we
// build a tiny inline one so we don't have to re-route through JsonModal which
// expects JSON content).
const B64ImageStrip = ({ images }) => {
  const [zoom, setZoom] = React.useState(null);
  if (!images || images.length === 0) return null;
  return (
    <div className="b64-strip" style={{ marginBottom: 10 }}>
      <div className="micro" style={{ marginBottom: 6, color: 'var(--fg-3)' }}>
        Images · {images.length}
      </div>
      <div className="b64-grid">
        {images.map((img, i) => {
          const src = `data:${guessImageMime(img.b64)};base64,${img.b64}`;
          return (
            <button
              key={i}
              type="button"
              className="b64-cell"
              onClick={() => setZoom({ src, caption: img.revisedPrompt })}
              title={img.revisedPrompt || `Image ${i + 1}`}>
              <img src={src} alt={img.revisedPrompt || `image-${i + 1}`} loading="lazy" />
              {img.revisedPrompt && (
                <span className="b64-cell-caption">{img.revisedPrompt}</span>
              )}
            </button>
          );
        })}
      </div>
      {zoom && typeof document !== 'undefined' && createPortal(
        <div
          className="b64-zoom-backdrop"
          onClick={() => setZoom(null)}>
          <div className="b64-zoom-stage" onClick={(e) => e.stopPropagation()}>
            <img src={zoom.src} alt={zoom.caption || 'image'}/>
            {zoom.caption && <div className="b64-zoom-caption">{zoom.caption}</div>}
            <div className="b64-zoom-actions">
              <a className="btn btn-sm" download={`image-${Date.now()}.png`} href={zoom.src}>
                <XIcons.IconDownload size={11}/> Download
              </a>
              <button className="btn btn-sm" onClick={() => setZoom(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Reused by both Try-It (this page) and the Tasks tab in console.
export const TaskResultView = ({ result, error, status, onOpenJson }) => {
  if (status === 'failed' || status === 'expired') {
    return (
      <div style={{ padding: 14, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: 'var(--err, #f87171)' }}>
        <div className="mono" style={{ fontSize: 11, marginBottom: 4 }}>{(error && error.code) || 'unknown_error'}</div>
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>{(error && error.message) || 'Task failed'}</div>
      </div>
    );
  }
  if (!result) return null;
  // Shape 1: top-level file (R2-stored / oversized JSON proxy result).
  if (result.type === 'file') {
    return <TaskFileRenderer url={result.url} mime={result.mime_type} bytes={result.bytes} expired={!!result.expired} />;
  }
  // Shape 2 (V2): nested file inside json body — UPSTREAM_POLLING video etc.
  if (result.type === 'json'
      && result.body && typeof result.body === 'object'
      && result.body.type === 'file'
      && result.body.url) {
    return <TaskFileRenderer url={result.body.url} mime={result.body.mime_type} bytes={result.body.bytes} expired={false} />;
  }
  // type === 'json' — inline body. Surface any b64_json images above the raw
  // JSON so users see the visual result first; raw payload stays available below.
  const images = extractB64Images(result.body);
  return (
    <div className="col gap-2">
      {result.status_code != null && (
        <div className="row gap-2" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          <span className="tag mono">{result.status_code}</span>
          {result.headers && result.headers['content-type'] && <span className="mono">{result.headers['content-type']}</span>}
        </div>
      )}
      <B64ImageStrip images={images}/>
      <XPrim.JsonViewer value={result.body} maxHeight={320} />
    </div>
  );
};

// Async-mode tag — shown next to endpoint names anywhere we expose the endpoint's
// callable surface (sidebar list, mobile picker, endpoint header). REQUIRED renders
// in mint (must use async); OPTIONAL renders in a quieter slate so it reads as
// "available" rather than mandatory.
const AsyncTag = ({ mode, size = 'sm' }) => {
  const { t } = useT();
  if (!mode || mode === 'NONE') return null;
  const required = mode === 'REQUIRED';
  const padding = size === 'lg' ? '4px 8px' : '1px 6px';
  const fontSize = size === 'lg' ? 10 : 9;
  const color = required ? 'var(--c-mint)' : 'var(--fg-2)';
  const bg = required ? 'rgba(46,204,113,0.10)' : 'var(--bg-3)';
  const border = required ? 'rgba(46,204,113,0.35)' : 'var(--line-2)';
  const label = required ? t('apidetail.tag.asyncRequired') : t('apidetail.tag.async');
  return (
    <span className="mono" title={label} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding, fontSize,
      letterSpacing: '0.06em', textTransform: 'uppercase',
      color, background: bg, border: `1px solid ${border}`, borderRadius: 4,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      <XIcons.IconZap size={size === 'lg' ? 10 : 8} />
      {label}
    </span>
  );
};

// Inline skeleton bar — used across the page header, stats block, and
// endpoint sidebar while the real /api-services/:id fetch is in flight.
// Reuses the global `.skeleton` shimmer class so the visual matches the
// rest of the app's loading states (Console overview, etc.).
const Skel = ({ w = 80, h = 14, style }) => (
  <span
    className="skeleton"
    style={{ display: 'inline-block', width: w, height: h, borderRadius: 4, verticalAlign: 'middle', ...style }}
  />
);

const fmtUsd = (value, { max = 6, min = 0, nullLabel = '—' } = {}) => {
  if (value == null || value === '') return nullLabel;
  const n = Number(value);
  if (!Number.isFinite(n)) return nullLabel;
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  })}`;
};

const fmtNumber = (value, nullLabel = '—') => {
  if (value == null || value === '') return nullLabel;
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US') : String(value);
};

const fmtTokenPrice = (value) => fmtUsd(Number(value || 0) * 1_000_000, { max: 6, nullLabel: '—' });

const endpointBillingRuntimeText = (ep) => {
  if (!ep) return '';
  if (isFreeEndpoint(ep)) return 'free';
  if (ep?.protocol === 'WEBSOCKET') return wsBillingRuntimeText(ep);
  if (ep.billingType === 'PER_TOKEN') return 'metered by token usage';
  if (ep.billingType === 'PER_RESOURCE') return `${fmtUsd(ep.costPerResource, { max: 6, nullLabel: '—' })} × resource count`;
  if (ep.billingType === 'DYNAMIC') return 'computed by dynamic rules';
  return `${fmtUsd(ep.costPerCall, { max: 6, nullLabel: '$0' })} per call`;
};

// ────────────────────────────────────────────────────────────────────────────
// WebSocket 计费展示 —— WS 端点的计费口径不在 billingType（那个字段对 WS 无意义，恒为
// PER_CALL），而在独立的 wsBilling JSON（kind + 各档单价）。以下 helper 把 wsBilling 归一
// 成 chip / 详情面板可直接消费的形状。单价一律换算成人类可读单位（token→/1M、char→/1K）。
const CHIP_TOKEN = { color: 'var(--ok)', bg: 'rgba(46,204,113,0.08)', border: 'rgba(46,204,113,0.28)' };
const CHIP_FIXED = { color: 'var(--warn)', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.28)' };

// USD/token → "$X/1M"（模态单价复用 fmtTokenPrice 的 ×1e6 口径，与 HTTP PER_TOKEN 表一致）。
const fmtPerMillion = (value) => fmtTokenPrice(value);
// USD/char → "$X/1K"（每字符单价极小，按千字符展示更可读）。
const fmtPerThousand = (value) => fmtUsd(Number(value || 0) * 1_000, { max: 6, nullLabel: '—' });

// realtime-token 的模态单价行（USD/token）。优先分档，未配则回退 mixed in/out。
const WS_MODAL_ROWS = [
  { key: 'inputTextTokenUsd', label: 'Input · text' },
  { key: 'inputAudioTokenUsd', label: 'Input · audio' },
  { key: 'inputCachedTokenUsd', label: 'Input · cached' },
  { key: 'outputTextTokenUsd', label: 'Output · text' },
  { key: 'outputAudioTokenUsd', label: 'Output · audio' },
];

const wsBilling = (ep) => (ep && ep._raw && ep._raw.wsBilling) || null;

// realtime-token 端点的最低正单价（USD/token），用作 chip 上的 "from $X/1M" 预览。
const wsMinTokenRate = (b) => {
  if (!b) return null;
  const rates = [...WS_MODAL_ROWS.map((r) => b[r.key]), b.inputTokenUsd, b.outputTokenUsd]
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  return rates.length ? Math.min(...rates) : null;
};

// WS 计费 → chip 的 { style, label, value, title }。protocol=WEBSOCKET 时用。
const wsChipInfo = (ep) => {
  const b = wsBilling(ep);
  const kind = b?.kind;
  if (isOpenAiLiveEndpoint(ep)) {
    const v = b?.perMinuteUsd != null
      ? `from ${fmtUsd(b.perMinuteUsd, { max: 6, nullLabel: '$0' })}/min`
      : 'usage-based';
    return {
      ...CHIP_TOKEN,
      label: 'Live + delegation',
      value: v,
      title: 'Live connection minutes are always billed. Client mode adds separate AI Gateway billing; Official mode adds managed Responses token billing.',
    };
  }
  if (kind === 'duration') {
    const v = b.perMinuteUsd != null ? `${fmtUsd(b.perMinuteUsd, { max: 6, nullLabel: '$0' })}/min` : 'per-minute';
    return { ...CHIP_FIXED, label: 'Per minute', value: v, title: 'Billed on connection duration, per minute of audio streamed.' };
  }
  if (kind === 'per-char') {
    const v = b.perCharUsd != null ? `${fmtPerThousand(b.perCharUsd)}/1K` : 'per-char';
    return { ...CHIP_FIXED, label: 'Per character', value: v, title: 'Billed on uplink text characters (per 1,000 characters shown).' };
  }
  if (kind === 'realtime-token') {
    const min = wsMinTokenRate(b);
    const v = min != null ? `from ${fmtPerMillion(min)}/1M` : 'usage-based';
    return { ...CHIP_TOKEN, label: 'Per token', value: v, title: 'Variable price — metered on realtime token usage (per modality). See pricing below.' };
  }
  // wsBilling 缺失/未知 kind：明确标注而非误报 $0。
  return { ...CHIP_TOKEN, label: 'Realtime', value: 'usage-based', title: 'WebSocket endpoint — metered on realtime usage.' };
};

// 左列 / runtime 文案。
const wsBillingRuntimeText = (ep) => {
  const b = wsBilling(ep);
  if (b?.kind === 'duration') return b.perMinuteUsd != null ? `${fmtUsd(b.perMinuteUsd, { max: 6, nullLabel: '$0' })}/min` : 'per-minute';
  if (b?.kind === 'per-char') return b.perCharUsd != null ? `${fmtPerThousand(b.perCharUsd)}/1K chars` : 'per-char';
  return 'per-token';
};

// 左列列表的短 cost 串（method 行右侧）。
const wsListCost = (ep) => {
  const b = wsBilling(ep);
  if (b?.kind === 'duration') return b.perMinuteUsd != null ? `${fmtUsd(b.perMinuteUsd, { max: 6, nullLabel: '$0' })}/min` : 'per-min';
  if (b?.kind === 'per-char') return b.perCharUsd != null ? `${fmtPerThousand(b.perCharUsd)}/1K` : 'per-char';
  return 'per-token';
};

const ModelChips = ({ models, limit = 8, compact = false }) => {
  const [open, setOpen] = udS(false);
  const list = [...new Set((models || []).filter(Boolean))];
  if (!list.length) return null;
  const collapsible = list.length > limit;
  const shown = open || !collapsible ? list : list.slice(0, limit);
  const hidden = list.length - shown.length;
  return (
    <div className="row" style={{flexWrap:'wrap', gap: compact ? 4 : 6, marginTop: compact ? 6 : 12, alignItems:'center'}}>
      <span style={{fontSize: compact ? 11 : 11.5, color:'var(--fg-4)', flexShrink: 0}}>
        支持模型{collapsible ? ` · ${list.length}` : ''}
      </span>
      {shown.map(m => (
        <span key={m} className="mono" title={m} style={{
          fontSize: compact ? 11 : 11.5,
          padding: compact ? '1px 7px' : '3px 9px',
          maxWidth: compact ? 160 : 240,
          borderRadius: 6,
          background:'var(--bg-2)',
          border:'1px solid var(--line)',
          color:'var(--fg-2)',
          overflow:'hidden',
          textOverflow:'ellipsis',
          whiteSpace:'nowrap',
        }}>{m}</span>
      ))}
      {collapsible && (
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="btn btn-sm btn-ghost"
          style={{height: compact ? 22 : 26, padding: compact ? '0 7px' : '0 9px', fontSize: compact ? 11 : 12}}
        >
          {open ? '收起' : `+${hidden} 更多`}
          <XIcons.IconChevronDown size={10} style={{transform: open ? 'rotate(180deg)' : 'none', transition:'transform .15s'}} />
        </button>
      )}
    </div>
  );
};

// Color-coded billing chip — emerald for PER_TOKEN (variable cost), amber for fixed price modes.
// Reused in the left endpoint list, the right-pane endpoint header, and the per-endpoint cost
// readout under the Send button.
//
// Two-part design (label + value) inspired by xapi-new ApiDetailPage.tsx:758:
//   - Bold label tells the user WHAT pricing model applies (PER CALL / PER TOKEN / PER RESOURCE)
//   - Mono value tells them HOW MUCH (or "usage-based" when the cost is variable)
//   - title= attribute gives a one-line plain-English explanation on hover
const BillingChip = ({ ep, size = 'sm' }) => {
  const lg = size === 'lg';
  const compact = size === 'compact';
  const padBox = lg ? '5px 10px' : compact ? '2px 6px' : '2px 6px';
  const labelFs = lg ? 10 : 9;
  const valueFs = lg ? 12 : 10;
  const labelGap = lg ? 6 : 4;

  const wrap = (color, bg, border, label, value, title) => (
    <span title={title || `${label}: ${value}`} style={{
      display:'inline-flex', alignItems:'center', gap: labelGap,
      padding: padBox, color, background: bg, border: `1px solid ${border}`,
      borderRadius: 4, lineHeight: 1.4, whiteSpace:'nowrap', flexShrink: 0,
    }}>
      {!compact && label && <span style={{fontSize: labelFs, fontWeight: 600, letterSpacing:'0.06em', textTransform:'uppercase', opacity: 0.85}}>{label}</span>}
      <span className="mono" style={{fontSize: valueFs}}>{value}</span>
    </span>
  );

  if (isFreeEndpoint(ep)) {
    return wrap('var(--ok)', 'rgba(46,204,113,0.08)', 'rgba(46,204,113,0.28)', '', 'free', 'No charge for this endpoint.');
  }
  if (ep.protocol === 'WEBSOCKET') {
    const w = wsChipInfo(ep);
    return wrap(w.color, w.bg, w.border, w.label, w.value, w.title);
  }
  if (ep.billingType === 'PER_TOKEN') {
    // For PER_TOKEN with model pricing, surface the cheapest model's input rate as a preview.
    let preview = 'usage-based';
    if (ep.tokenPricing && typeof ep.tokenPricing === 'object') {
      const rates = Object.values(ep.tokenPricing).map(p => Number(p?.inputPricePerToken || 0)).filter(Number.isFinite).filter(n => n > 0);
      if (rates.length) {
        preview = `from ${fmtTokenPrice(Math.min(...rates))}/1M`;
      }
    }
    return wrap('var(--ok)', 'rgba(46,204,113,0.08)', 'rgba(46,204,113,0.28)', 'Per token', preview, 'Variable price — billed on response token usage. See Token Pricing for per-model rates.');
  }
  if (ep.billingType === 'PER_RESOURCE') {
    const value = ep.costPerResource != null && Number(ep.costPerResource) >= 0 ? `${fmtUsd(ep.costPerResource)} each` : 'per item';
    const path = ep.resourceCountJsonPath || ep._raw?.resourceCountJsonPath || 'data.length';
    return wrap('var(--warn)', 'rgba(245,158,11,0.08)', 'rgba(245,158,11,0.28)', 'Per resource', value, `Charged per item returned. Counted via ${path}.`);
  }
  if (ep.billingType === 'DYNAMIC') {
    // Variable price computed by a per-request rule engine — never a flat "$0/free".
    return wrap('var(--ok)', 'rgba(46,204,113,0.08)', 'rgba(46,204,113,0.28)', 'Dynamic', 'rule-based', 'Variable price computed per request by a rule engine (model, tokens, resolution, …).');
  }
  // PER_CALL (fallback)
  const value = fmtUsd(ep.costPerCall);
  return wrap('var(--warn)', 'rgba(245,158,11,0.08)', 'rgba(245,158,11,0.28)', 'Per call', value, 'Fixed price per request, debited atomically before the call is forwarded.');
};

// Token pricing table — model × (input $/1M, output $/1M). Shown for PER_TOKEN endpoints.
// 长列表（模型多）默认只显示前 COLLAPSED_LIMIT 行，其余折叠，点「查看全部」展开。
const TokenPricingTable = ({ pricing }) => {
  const [open, setOpen] = udS(false);
  if (!pricing || typeof pricing !== 'object' || !Object.keys(pricing).length) return null;
  const entries = Object.entries(pricing).sort(([a], [b]) => (a === 'default' ? -1 : b === 'default' ? 1 : a.localeCompare(b)));
  const COLLAPSED_LIMIT = 5;
  const collapsible = entries.length > COLLAPSED_LIMIT;
  const shown = open || !collapsible ? entries : entries.slice(0, COLLAPSED_LIMIT);
  const hiddenCount = entries.length - shown.length;
  const hasCacheWrite = entries.some(([, p]) => p?.cacheCreationPricePerToken != null);
  const hasCacheRead = entries.some(([, p]) => p?.cacheReadPricePerToken != null);
  const hasTiers = entries.some(([, p]) => Array.isArray(p?.tiers) && p.tiers.length > 0);
  const gridCols = [
    'minmax(120px, 1.4fr)',
    '92px',
    '92px',
    hasCacheWrite ? '92px' : null,
    hasCacheRead ? '92px' : null,
    hasTiers ? '74px' : null,
  ].filter(Boolean).join(' ');
  const cell = { textAlign:'right', minWidth: 0 };
  const tierLabel = (tier) => {
    const threshold = tier.inputTokenThreshold ?? tier.threshold ?? tier.minTokens;
    return [
      threshold != null ? `≥ ${fmtNumber(threshold)} tok` : null,
      `in ${fmtTokenPrice(tier.inputPricePerToken)}`,
      `out ${fmtTokenPrice(tier.outputPricePerToken)}`,
      tier.cacheCreationPricePerToken != null ? `cache write ${fmtTokenPrice(tier.cacheCreationPricePerToken)}` : null,
      tier.cacheReadPricePerToken != null ? `cache read ${fmtTokenPrice(tier.cacheReadPricePerToken)}` : null,
    ].filter(Boolean).join(' · ');
  };
  return (
    <div className="hairline" style={{borderRadius: 6, overflow:'hidden', background:'var(--bg-2)'}}>
      <div style={{display:'grid', gridTemplateColumns: gridCols, gap: 10, padding:'6px 10px', borderBottom:'1px solid var(--line)', fontSize: 10, color:'var(--fg-4)', textTransform:'uppercase', letterSpacing:'0.06em', alignItems:'center'}}>
        <span>Model{collapsible ? ` · ${entries.length}` : ''}</span>
        <span style={cell}>Input /1M</span>
        <span style={cell}>Output /1M</span>
        {hasCacheWrite && <span style={cell}>Cache write</span>}
        {hasCacheRead && <span style={cell}>Cache read</span>}
        {hasTiers && <span style={cell}>Tiers</span>}
      </div>
      {shown.map(([model, p]) => {
        const tiers = Array.isArray(p?.tiers) ? p.tiers : [];
        return (
          <div key={model} style={{borderBottom:'1px solid var(--line)'}}>
            <div style={{display:'grid', gridTemplateColumns: gridCols, gap: 10, padding:'8px 10px', fontSize: 12, alignItems:'center'}}>
              <span className="mono truncate" style={{color:'var(--fg-1)'}} title={model}>{model}</span>
              <span className="mono" style={{...cell, color:'var(--ok)'}}>{fmtTokenPrice(p?.inputPricePerToken)}</span>
              <span className="mono" style={{...cell, color:'var(--warn)'}}>{fmtTokenPrice(p?.outputPricePerToken)}</span>
              {hasCacheWrite && <span className="mono" style={{...cell, color:'var(--fg-3)'}}>{p?.cacheCreationPricePerToken != null ? fmtTokenPrice(p.cacheCreationPricePerToken) : '—'}</span>}
              {hasCacheRead && <span className="mono" style={{...cell, color:'var(--fg-3)'}}>{p?.cacheReadPricePerToken != null ? fmtTokenPrice(p.cacheReadPricePerToken) : '—'}</span>}
              {hasTiers && <span className="mono" style={{...cell, color: tiers.length ? 'var(--fg-2)' : 'var(--fg-4)'}}>{tiers.length || '—'}</span>}
            </div>
            {tiers.length > 0 && (
              <div style={{padding:'0 10px 8px 10px'}}>
                <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', display:'flex', flexDirection:'column', gap: 3}}>
                  {tiers.map((tier, i) => <span key={i}>{tierLabel(tier)}</span>)}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {collapsible && (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            width:'100%', padding:'8px 10px', border:'none', background:'transparent',
            color:'var(--fg-3)', fontSize: 12, cursor:'pointer', textAlign:'center',
            display:'flex', alignItems:'center', justifyContent:'center', gap: 5,
          }}
        >
          {open ? '收起' : `查看全部 ${entries.length} 个模型（+${hiddenCount}）`}
          <XIcons.IconChevronDown size={11} style={{transform: open ? 'rotate(180deg)' : 'none', transition:'transform .15s'}}/>
        </button>
      )}
    </div>
  );
};

const BillingInfoBox = ({ title, formula, meta, children }) => (
  <div style={{marginTop: 14, border:'1px solid var(--line)', borderRadius: 6, background:'var(--bg-2)', overflow:'hidden'}}>
    <div className="row between" style={{padding:'8px 10px', borderBottom:'1px solid var(--line)', gap: 10, alignItems:'center'}}>
      <div style={{minWidth: 0}}>
        <div className="micro" style={{marginBottom: 3}}>{title}</div>
        {meta && <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', lineHeight: 1.45}}>{meta}</div>}
      </div>
      {formula && (
        <span className="mono" style={{
          fontSize: 12,
          color:'var(--fg-1)',
          padding:'4px 8px',
          borderRadius: 5,
          background:'var(--bg-1)',
          border:'1px solid var(--line)',
          whiteSpace:'nowrap',
        }}>{formula}</span>
      )}
    </div>
    {children && <div style={{padding: 10}}>{children}</div>}
  </div>
);

const DynamicPricingPanel = ({ rules }) => {
  const [openRules, setOpenRules] = udS(false);
  if (!rules || typeof rules !== 'object') {
    return (
      <BillingInfoBox
        title="Dynamic pricing"
        formula="rule engine"
        meta="No public formula is attached to this endpoint."
      />
    );
  }
  const U = deserializeRules(rules);
  const fields = Array.isArray(U.fields) ? U.fields : [];
  const ruleRows = Array.isArray(U.rules) ? U.rules : [];
  const shownRules = openRules || ruleRows.length <= 3 ? ruleRows : ruleRows.slice(0, 3);
  const costCaps = [
    U.minCost !== '' && U.minCost != null ? `min ${fmtUsd(U.minCost)}` : null,
    U.maxCost !== '' && U.maxCost != null ? `max ${fmtUsd(U.maxCost)}` : null,
  ].filter(Boolean).join(' · ');
  const sourceLabel = (f) => [ROOTLABEL[f.source] || f.source, f.path].filter(Boolean).join('.');
  const fieldMeta = (f) => [
    f.type,
    f.estTyp !== '' && f.estTyp != null ? `typ ${fmtNumber(f.estTyp)}` : null,
    f.estMax !== '' && f.estMax != null ? `max ${fmtNumber(f.estMax)}` : null,
    f.default !== '' && f.default != null ? `default ${fmtNumber(f.default)}` : null,
    f.clampMin !== '' && f.clampMin != null ? `min ${fmtNumber(f.clampMin)}` : null,
    f.clampMax !== '' && f.clampMax != null ? `max ${fmtNumber(f.clampMax)}` : null,
  ].filter(Boolean).join(' · ');
  const mapDetails = (rule) =>
    (rule.items || [])
      .flatMap((it) => it.factors || [])
      .filter((f) => f.kind === 'map' && f.field)
      .map((f) => {
        const opts = (f.options || []).filter((o) => o.key !== '' && o.key != null);
        const bits = opts.slice(0, 4).map((o) => `${o.key}=${fmtUsd(o.price)}`);
        if (opts.length > 4) bits.push(`+${opts.length - 4}`);
        if (f.onUnmatched?.mode === 'default') bits.push(`else=${fmtUsd(f.onUnmatched.price)}`);
        if (f.onUnmatched?.mode === 'reject') bits.push('else=reject');
        return `${f.field}: ${bits.join(' · ')}`;
      });
  return (
    <BillingInfoBox
      title="Dynamic pricing"
      formula="first matching rule"
      meta={[
        `${ruleRows.length} rule${ruleRows.length === 1 ? '' : 's'}`,
        `${fields.length} field${fields.length === 1 ? '' : 's'}`,
        U.holdPolicy ? `hold ${U.holdPolicy}` : null,
        costCaps || null,
      ].filter(Boolean).join(' · ')}
    >
      {fields.length > 0 && (
        <div style={{marginBottom: 10}}>
          <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', marginBottom: 5}}>Fields</div>
          <div className="col gap-1">
            {fields.map((f) => (
              <div key={f.name} className="row gap-2" style={{fontSize: 12, color:'var(--fg-3)', alignItems:'baseline', minWidth: 0}}>
                <span className="mono" style={{color:'var(--fg-1)', minWidth: 86, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={f.name}>{f.name}</span>
                <span className="mono" style={{flex: 1, minWidth: 0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={sourceLabel(f)}>{sourceLabel(f)}</span>
                <span className="mono" style={{flexShrink: 0, color:'var(--fg-4)'}}>{fieldMeta(f)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="col gap-2">
        {shownRules.map((rule, i) => {
          const preview = numericPreview(rule, fields, { minCost: U.minCost, maxCost: U.maxCost });
          const maps = mapDetails(rule);
          return (
            <div key={i} style={{border:'1px solid var(--line)', borderRadius: 6, background:'var(--bg-1)', padding: 10}}>
              <div className="row between" style={{gap: 8, alignItems:'baseline', marginBottom: 6}}>
                <span className="mono" style={{fontSize: 11, color:'var(--fg-4)'}}>Rule {i + 1}</span>
                <span style={{fontSize: 11, color:'var(--fg-3)', textAlign:'right'}}>{ruleLabel(rule)}</span>
              </div>
              <div className="mono" style={{fontSize: 12, color:'var(--fg-1)', lineHeight: 1.6, overflowWrap:'anywhere'}}>
                {previewStr(rule)}
              </div>
              {preview.ok && (
                <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', marginTop: 6, lineHeight: 1.5, overflowWrap:'anywhere'}}>
                  sample {preview.summary || 'constants'} → {preview.exprStr} = <span style={{color:'var(--ok)'}}>{fmtUsd(preview.result)}</span>
                </div>
              )}
              {maps.length > 0 && (
                <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', marginTop: 5, lineHeight: 1.5}}>
                  {maps.map((m) => <div key={m}>{m}</div>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {ruleRows.length > 3 && (
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpenRules(v => !v)} style={{marginTop: 8}}>
          {openRules ? '收起规则' : `查看全部 ${ruleRows.length} 条规则`}
          <XIcons.IconChevronDown size={10} style={{transform: openRules ? 'rotate(180deg)' : 'none', transition:'transform .15s'}} />
        </button>
      )}
    </BillingInfoBox>
  );
};

// WebSocket 定价详情面板 —— 按 wsBilling.kind 渲染。realtime-token 出一张模态单价表
// （USD/1M tokens，与 HTTP PER_TOKEN 表口径一致）；duration / per-char 出公式 + 单价。
const WsPricingPanel = ({ ep }) => {
  const b = wsBilling(ep);
  if (!b || !b.kind) {
    return (
      <BillingInfoBox
        title="Realtime pricing"
        formula="usage-based"
        meta="This WebSocket endpoint is metered on realtime usage. No public rate is attached."
      />
    );
  }
  if (isOpenAiLiveEndpoint(ep)) {
    const wsConfig = ep?._raw?.wsConfig || {};
    const delegation = wsConfig.openAiLiveDelegation || {};
    const delegationType = delegation.type ?? 'client';
    const clientAvailable = delegationType === 'client' || delegationType === 'selectable';
    const officialAvailable = delegationType === 'responses' || delegationType === 'selectable';
    const rows = officialAvailable ? openAiLiveDelegationPricingRows(b) : [];
    const authorization = resolveOpenAiLiveAuthorizationDisplay(ep);
    const threshold = Number(b.delegationLongContextThresholdTokens);
    const inputMultiplier = Number(b.delegationLongContextInputMultiplier);
    const outputMultiplier = Number(b.delegationLongContextOutputMultiplier);
    const hasLongContextTier =
      Number.isFinite(threshold) && threshold > 0 &&
      Number.isFinite(inputMultiplier) && inputMultiplier >= 1 &&
      Number.isFinite(outputMultiplier) && outputMultiplier >= 1;
    const cell = { textAlign: 'right', minWidth: 0 };
    return (
      <BillingInfoBox
        title="Live + delegation pricing"
        formula="Live minutes + delegation usage"
        meta="Every session is billed for the Live connection. Delegation billing depends on the selected mode."
      >
        <div className="row gap-3" style={{ fontSize: 12, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
          <span>Live rate <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtUsd(b.perMinuteUsd, { max: 6, nullLabel: '—' })}/min</span></span>
          <span>Initial connection authorization <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtUsd(authorization.initialAuthorizationUsd, { max: 6, nullLabel: '—' })}</span></span>
          {authorization.maxSessionAuthorizationUsd != null && (
            <span>Session authorization ceiling <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtUsd(authorization.maxSessionAuthorizationUsd, { max: 6, nullLabel: '—' })}</span></span>
          )}
        </div>
        <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--fg-4)', lineHeight: 1.5 }}>
          Authorizations are temporary balance reservations, not final charges. The initial amount covers up to five minutes of Live usage; unused funds are released at settlement.
        </div>
        {clientAvailable && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.5 }}>
            <span className="mono" style={{ color: 'var(--fg-1)' }}>Client mode</span> · The selected xAPI AI Gateway model call is billed separately at that model's published price.
          </div>
        )}
        {officialAvailable && (
          <div style={{ marginTop: 10 }}>
            <div className="micro" style={{ marginBottom: 6 }}>Official delegation · USD / 1M tokens</div>
            {authorization.officialStartAuthorizationUsd != null ? (
              <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.5 }}>
                Before an Official session starts, up to <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtUsd(authorization.officialStartAuthorizationUsd, { max: 6, nullLabel: '—' })}</span> is temporarily authorized to cover one bounded Responses request plus the rolling Live window.
              </div>
            ) : (
              <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--warn)', lineHeight: 1.5 }}>
                Official mode requires an additional temporary authorization before the session starts; this backend did not publish the exact amount.
              </div>
            )}
            {rows.length ? (
              <div className="hairline" style={{ borderRadius: 6, overflow: 'hidden', background: 'var(--bg-2)' }}>
                {rows.map((row) => (
                  <div key={row.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px,1.4fr) 110px', gap: 10, padding: '8px 10px', fontSize: 12, alignItems: 'center', borderBottom: '1px solid var(--line)' }}>
                    <span className="mono" style={{ color: 'var(--fg-1)' }}>{row.label}</span>
                    <span className="mono" style={{ ...cell, color: 'var(--ok)' }}>{fmtPerMillion(row.value)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--fg-4)' }}>No managed Responses token rate is published for this endpoint.</div>
            )}
            {hasLongContextTier && (
              <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--fg-4)', lineHeight: 1.5 }}>
                Above {fmtNumber(threshold)} input tokens in one delegated response: input ×{inputMultiplier}, output ×{outputMultiplier}.
              </div>
            )}
          </div>
        )}
      </BillingInfoBox>
    );
  }
  if (b.kind === 'duration') {
    return (
      <BillingInfoBox
        title="Per-minute pricing"
        formula={`minutes × ${fmtUsd(b.perMinuteUsd, { max: 6, nullLabel: '—' })}`}
        meta="Billed on connection duration — the gateway meters audio streamed and charges per minute."
      >
        <div className="row gap-3" style={{ fontSize: 12, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
          <span>Rate <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtUsd(b.perMinuteUsd, { max: 6, nullLabel: '—' })}/min</span></span>
          {b.holdUsd != null && <span>Hold <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtUsd(b.holdUsd, { max: 6, nullLabel: '—' })}</span></span>}
        </div>
      </BillingInfoBox>
    );
  }
  if (b.kind === 'per-char') {
    return (
      <BillingInfoBox
        title="Per-character pricing"
        formula={`characters × ${fmtUsd(b.perCharUsd, { max: 8, nullLabel: '—' })}`}
        meta="Billed on uplink text characters — the character count of the text you send is metered."
      >
        <div className="row gap-3" style={{ fontSize: 12, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
          <span>Rate <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtPerThousand(b.perCharUsd)}/1K chars</span></span>
          {b.holdUsd != null && <span>Hold <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtUsd(b.holdUsd, { max: 6, nullLabel: '—' })}</span></span>}
        </div>
      </BillingInfoBox>
    );
  }
  // realtime-token —— 模态分档单价（优先）或回退 mixed in/out，统一 USD/1M 展示。
  const modalRows = WS_MODAL_ROWS.filter((r) => b[r.key] != null && Number(b[r.key]) > 0);
  const mixedRows = [
    b.inputTokenUsd != null ? { label: 'Input (all)', v: b.inputTokenUsd } : null,
    b.outputTokenUsd != null ? { label: 'Output (all)', v: b.outputTokenUsd } : null,
  ].filter(Boolean);
  const rows = modalRows.length
    ? modalRows.map((r) => ({ label: r.label, v: b[r.key] }))
    : mixedRows;
  const cell = { textAlign: 'right', minWidth: 0 };
  return (
    <BillingInfoBox
      title="Realtime token pricing"
      formula="tokens × price per modality"
      meta="Charged from the upstream usage block. Prices below are USD per 1M tokens."
    >
      {rows.length ? (
        <div className="hairline" style={{ borderRadius: 6, overflow: 'hidden', background: 'var(--bg-2)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px,1.4fr) 110px', gap: 10, padding: '6px 10px', borderBottom: '1px solid var(--line)', fontSize: 10, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <span>Modality</span>
            <span style={cell}>Price /1M</span>
          </div>
          {rows.map((r) => (
            <div key={r.label} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px,1.4fr) 110px', gap: 10, padding: '8px 10px', fontSize: 12, alignItems: 'center', borderBottom: '1px solid var(--line)' }}>
              <span className="mono" style={{ color: 'var(--fg-1)' }}>{r.label}</span>
              <span className="mono" style={{ ...cell, color: 'var(--ok)' }}>{fmtPerMillion(r.v)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--fg-4)' }}>No per-modality rate is published for this endpoint.</div>
      )}
      {b.holdUsd != null && (
        <div className="row gap-2" style={{ marginTop: 8, fontSize: 12, color: 'var(--fg-3)' }}>
          <span>Pre-auth hold <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtUsd(b.holdUsd, { max: 6, nullLabel: '—' })}</span></span>
        </div>
      )}
    </BillingInfoBox>
  );
};

const DynamicBillingDetails = ({ ep }) => {
  const { t } = useT();
  const rules = ep.dynamicRules || ep._raw?.dynamicRules;
  if (!hasModelReferenceQuotes(ep.modelQuotes)) return <DynamicPricingPanel rules={rules} />;
  return (
    <div className="col gap-2">
      <ModelReferencePrices quotes={ep.modelQuotes} />
      <details>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--fg-3)', padding: '6px 0' }}>{t('api.referencePrices.rules')}</summary>
        <DynamicPricingPanel rules={rules} />
      </details>
    </div>
  );
};

const BillingDetailsPanel = ({ ep }) => {
  if (!ep) return null;
  if (isFreeEndpoint(ep)) return <BillingInfoBox title="Pricing" formula="free" meta="No charge for this endpoint." />;
  if (ep.protocol === 'WEBSOCKET') return <WsPricingPanel ep={ep} />;
  if (ep.billingType === 'PER_TOKEN') {
    const entries = ep.tokenPricing && typeof ep.tokenPricing === 'object' ? Object.values(ep.tokenPricing) : [];
    const hasCache = entries.some((p) => p?.cacheReadPricePerToken != null || p?.cacheCreationPricePerToken != null);
    const hasPricing = entries.length > 0;
    return (
      <BillingInfoBox
        title="Token pricing"
        formula={`input × price + output × price${hasCache ? ' + cache' : ''}`}
        meta="Charged from the upstream usage block. Prices below are USD per 1M tokens."
      >
        {hasPricing ? (
          <TokenPricingTable pricing={ep.tokenPricing} />
        ) : (
          <div style={{fontSize: 12, color:'var(--fg-4)'}}>No model pricing is published for this endpoint.</div>
        )}
      </BillingInfoBox>
    );
  }
  if (ep.billingType === 'PER_RESOURCE') {
    const path = ep.resourceCountJsonPath || ep._raw?.resourceCountJsonPath || 'data.length';
    const max = ep.estimatedMaxResources ?? ep._raw?.estimatedMaxResources;
    return (
      <BillingInfoBox
        title="Per-resource pricing"
        formula={`count × ${fmtUsd(ep.costPerResource, { max: 6, nullLabel: '—' })}`}
        meta="The gateway reads a count from the response and multiplies it by the unit price."
      >
        <div className="row gap-2" style={{fontSize: 12, color:'var(--fg-3)', flexWrap:'wrap'}}>
          <span>Count JSON path <span className="mono" style={{color:'var(--fg-1)'}}>{path}</span></span>
          {max != null && <span>Estimated max <span className="mono" style={{color:'var(--fg-1)'}}>{fmtNumber(max)}</span></span>}
        </div>
      </BillingInfoBox>
    );
  }
  if (ep.billingType === 'DYNAMIC') {
    return <DynamicBillingDetails ep={ep} />;
  }
  return (
    <BillingInfoBox
      title="Per-call pricing"
      formula={fmtUsd(ep.costPerCall, { max: 6, nullLabel: '—' })}
      meta="A fixed amount is reserved and charged once per forwarded request."
    />
  );
};

// Header billing summary — handles mixed billing across endpoints.
//   single PER_TOKEN  → "Per token"
//   single PER_CALL   → "Per call"
//   single PER_RESOURCE → "Per resource"
//   mixed             → "Mixed" (with hover tooltip listing the breakdown)
const headerBillingLabel = (endpoints) => {
  if (!endpoints || !endpoints.length) return { label: '—', tooltip: '' };
  if (endpoints.every(isFreeEndpoint)) return { label: 'free', tooltip: 'All endpoints are free' };
  // WS 端点按 wsBilling.kind 归桶（duration/token/char），HTTP 端点按 billingType 归桶。
  const counts = endpoints.reduce((acc, ep) => {
    let t;
    if (ep.protocol === 'WEBSOCKET') {
      if (isOpenAiLiveEndpoint(ep)) t = 'WS_OPENAI_LIVE';
      else {
        const kind = wsBilling(ep)?.kind;
        t = kind === 'duration' ? 'WS_DURATION' : kind === 'per-char' ? 'WS_CHAR' : 'WS_TOKEN';
      }
    } else {
      t = ep.billingType || 'PER_CALL';
    }
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const types = Object.keys(counts);
  if (types.length === 1) {
    const t = types[0];
    if (t === 'PER_TOKEN') return { label: 'Per token', tooltip: 'All endpoints metered on token usage' };
    if (t === 'PER_RESOURCE') return { label: 'Per resource', tooltip: 'All endpoints billed by resource count' };
    if (t === 'DYNAMIC') return { label: 'Dynamic', tooltip: 'All endpoints priced by a per-request rule engine' };
    if (t === 'WS_TOKEN') return { label: 'Per token', tooltip: 'All endpoints metered on realtime token usage' };
    if (t === 'WS_OPENAI_LIVE') return { label: 'Live + delegation', tooltip: 'Live minutes plus mode-dependent delegation billing' };
    if (t === 'WS_DURATION') return { label: 'Per minute', tooltip: 'All endpoints billed by connection duration' };
    if (t === 'WS_CHAR') return { label: 'Per character', tooltip: 'All endpoints billed by uplink character count' };
    return { label: 'Per call', tooltip: 'All endpoints billed by call count' };
  }
  const nameOf = (t) => ({
    WS_DURATION: 'minute', WS_TOKEN: 'token', WS_CHAR: 'char', WS_OPENAI_LIVE: 'live',
  }[t] || t.replace('PER_', '').toLowerCase());
  const breakdown = Object.entries(counts).map(([t, n]) => `${nameOf(t)}×${n}`).join(' · ');
  return { label: 'Mixed', tooltip: breakdown };
};

/** Marketplace status chip: prefer real service status; fall back to active. */
const headerStatusChip = (status) => {
  const s = String(status || 'active').toLowerCase();
  if (s === 'active' || s === '') return { text: 'active', tone: 'ok' };
  if (s === 'draft') return { text: 'draft', tone: 'muted' };
  if (s === 'sandbox' || s === 'pending_review' || s === 'in_review') return { text: s.replace(/_/g, ' '), tone: 'warn' };
  if (s === 'suspended' || s === 'rejected' || s === 'deleted') return { text: s, tone: 'err' };
  return { text: s, tone: 'muted' };
};

// Documentation tab — real auth header (xapi-key), real gateway base URL, real endpoint summary,
// real OpenAPI spec download, and the actual JSON error envelope returned by the gateway.
const DocsTab = ({ api, gwUrl, keyForDisplay, hasKey }) => {
  const apiBase = (XApi.config && XApi.config.apiBase) || '';
  const openapiUrl = apiBase + `/api-services/${api.id}/openapi`;
  const epCount = (api.endpoints || []).length;
  // Bucket endpoints by billing model so the Pricing section reflects what's actually wired.
  const billing = (api.endpoints || []).reduce((acc, ep) => {
    const t = ep._raw?.billingType || 'PER_CALL';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  return (
    <div style={{maxWidth: 820, lineHeight: 1.75, color:'var(--fg-2)'}}>
      <h2 style={{fontSize: 22, letterSpacing:'-0.01em', margin: '0 0 6px'}}>Getting started</h2>
      <p style={{margin:'0 0 24px', color:'var(--fg-3)', fontSize: 13}}>
        {api.name} runs through the xAPI gateway. One key, one base URL, every endpoint.
      </p>

      <h3 style={{marginTop: 28, marginBottom: 8, fontSize: 15, fontWeight: 500}}>Authentication</h3>
      <p style={{margin:'0 0 8px', fontSize: 13}}>
        Send your xAPI key in the <span className="mono">xapi-key</span> request header. Keys are scoped per user and rotate freely from the console.
      </p>
      <pre className="code">xapi-key: {hasKey ? keyForDisplay : '${XAPI_KEY}'}</pre>

      <h3 style={{marginTop: 28, marginBottom: 8, fontSize: 15, fontWeight: 500}}>Base URL</h3>
      <p style={{margin:'0 0 8px', fontSize: 13}}>
        Every endpoint hangs off this prefix. The gateway forwards to the upstream, applies billing, and signs traces.
      </p>
      <pre className="code">{gwUrl}</pre>

      <h3 style={{marginTop: 28, marginBottom: 8, fontSize: 15, fontWeight: 500}}>Endpoints</h3>
      <p style={{margin:'0 0 8px', fontSize: 13}}>
        {epCount} endpoint{epCount === 1 ? '' : 's'} exposed on version <span className="mono">{api.version || '—'}</span>. See the <span className="mono">Endpoints</span> tab for the full list, or pull the live OpenAPI spec:
      </p>
      <div className="row gap-2" style={{marginTop: 6}}>
        <a className="btn btn-sm" href={openapiUrl + '?format=json'} target="_blank" rel="noopener noreferrer">
          <XIcons.IconDownload size={12}/> openapi.json
        </a>
        <a className="btn btn-sm" href={openapiUrl + '?format=yaml'} target="_blank" rel="noopener noreferrer">
          <XIcons.IconDownload size={12}/> openapi.yaml
        </a>
      </div>

      <h3 style={{marginTop: 28, marginBottom: 8, fontSize: 15, fontWeight: 500}}>Pricing</h3>
      <p style={{margin:'0 0 8px', fontSize: 13}}>
        {[
          billing.PER_CALL ? `${billing.PER_CALL} per-call` : null,
          billing.PER_TOKEN ? `${billing.PER_TOKEN} token-priced` : null,
          billing.PER_RESOURCE ? `${billing.PER_RESOURCE} per-resource` : null,
          billing.DYNAMIC ? `${billing.DYNAMIC} dynamic` : null,
        ].filter(Boolean).join(' · ')}
        {!epCount && 'No pricing configured.'}
      </p>
      <p style={{margin:'0 0 8px', fontSize: 13}}>
        Per-call endpoints debit the listed cost atomically before forwarding. Token-priced endpoints meter on the upstream's <span className="mono">usage</span> response. Dynamic endpoints compute the price per request from a rule engine. See the per-row Cost column in the Endpoints tab.
      </p>

      <h3 style={{marginTop: 28, marginBottom: 8, fontSize: 15, fontWeight: 500}}>Errors</h3>
      <p style={{margin:'0 0 8px', fontSize: 13}}>
        The gateway propagates upstream HTTP status codes verbatim. Gateway-side errors (auth, billing, rate limit) use this envelope:
      </p>
      <pre className="code">{`{
  "error": "Insufficient balance",
  "code": "BILLING_INSUFFICIENT",
  "details": { "required": 0.01, "available": 0.003 }
}`}</pre>
      <p style={{margin:'8px 0 0', fontSize: 12, color:'var(--fg-4)'}}>
        Common codes: <span className="mono">AUTH_INVALID_KEY</span>, <span className="mono">BILLING_INSUFFICIENT</span>, <span className="mono">RATE_LIMIT</span>, <span className="mono">UPSTREAM_TIMEOUT</span>.
      </p>
    </div>
  );
};

// Changelog tab — backed by /api-services/:id/versions. Each ApiVersion's `description` field is
// the changelog body for that release. Empty state when only one version exists.
// Skill tab — skills.sh 风格布局：面包屑 + 大标题 + 分类 pill；主栏
// Installation(全宽命令条) → Summary → SKILL.md 直出；右栏 Installs 大数字 +
// Repository/Version/First published 元信息 + deps/permissions/examples。
// 复用 skills-market.jsx 的 .skills-detail-grid/.skills-md/.skills-command CSS，
// 新增 .skill-sh-* 少量样式（styles.css）。数据来自公开 GET /skills/:slug。
const SkillTabContent = ({ linkedSkill, navigate }) => {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [copied, copy] = XPrim.useCopy();

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    XApi.skillService.detail(linkedSkill.slug)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [linkedSkill.slug]);

  const installCmd = `npx skills add https://github.com/xapi-labs/skill-market/tree/main/skills/${linkedSkill.slug}`;
  const handleCopy = () => {
    copy(installCmd);
    XApi.skillService.recordInstall(linkedSkill.slug).catch(() => {});
  };

  if (loading) {
    return <div style={{ padding: '32px 0', color: 'var(--fg-3)' }}>Loading skill…</div>;
  }

  const name = detail?.name || linkedSkill.name;
  const description = detail?.description || linkedSkill.description;
  const version = detail?.latestVersion?.version || linkedSkill.latestVersion;
  const installCount = detail?.installCount ?? linkedSkill.installCount ?? 0;
  const categories = detail?.categories || [];
  const tags = detail?.tags || [];
  const manifest = detail?.latestVersion?.manifest || {};
  const readme = detail?.latestVersion?.readmeMarkdown || '';
  const deps = manifest.xapiDependencies || [];
  const permissionEntries = [
    ['writesExternalState', 'Writes external state'],
    ['mayChargeUserBalance', 'May spend credits/balance'],
    ['handlesPersonalData', 'Handles personal data'],
  ].filter(([key]) => Boolean(manifest.permissions?.[key]));
  const examples = Array.isArray(manifest.metadata?.xapi?.examples)
    ? manifest.metadata.xapi.examples
    : Array.isArray(manifest.examples)
      ? manifest.examples
      : [];

  const repo = detail?.repo || 'xapi-labs/skill-market';
  const repoOwner = repo.split('/')[0];
  const publishedAt = detail?.latestVersion?.publishedAt;
  const formatDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatCompact = (n) =>
    n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : String(n);

  // 布局参考 skills.sh 技能详情页：面包屑 + 大标题 + 分类 pill；主栏
  // Installation(全宽命令条) → Summary → SKILL.md 直出；右栏 Installs 大数字 +
  // Repository/Version/First published 元信息。我们没有的块（GitHub stars、
  // Security audits、安装量趋势图）直接省略。
  return (
    <div className="skill-sh">
      <div className="skill-sh-crumb">
        <a onClick={() => navigate('skills')}>skills</a>
        <span> / </span>
        <span>{repoOwner}</span>
        <span> / </span>
        <span>{linkedSkill.slug}</span>
      </div>

      <h1 className="skill-sh-title">{name}</h1>
      {(categories.length > 0 || tags.length > 0) && (
        <div className="row gap-2" style={{ flexWrap: 'wrap', marginBottom: 24 }}>
          {categories.map((c) => <span className="tag" key={`cat-${c}`}>{c}</span>)}
          {tags.map((t) => <span className="tag" style={{ color: 'var(--fg-3)' }} key={`tag-${t}`}>#{t}</span>)}
        </div>
      )}

      <section className="skills-detail-grid">
        <div className="skill-sh-main">
          <div className="skill-sh-section">
            <div className="micro-mono skill-sh-head">Installation</div>
            <div className="skills-command" style={{ margin: 0 }}>
              <code>$ {installCmd}</code>
              <button
                type="button"
                className="btn btn-sm btn-ghost btn-icon"
                onClick={handleCopy}
                title="Copy install command"
                aria-label="Copy install command"
              >
                {copied ? <XIcons.IconCheck size={12} /> : <XIcons.IconCopy size={12} />}
              </button>
            </div>
          </div>

          {description && (
            <div className="skill-sh-section">
              <div className="micro-mono skill-sh-head">Summary</div>
              <div className="card skill-sh-summary">
                <strong>{description}</strong>
              </div>
            </div>
          )}

          <div className="skill-sh-section">
            <div className="micro-mono skill-sh-head">SKILL.md</div>
            {readme.trim() ? (
              <div className="skills-md" dangerouslySetInnerHTML={{ __html: renderSkillMarkdown(readme) }} />
            ) : (
              <p style={{ color: 'var(--fg-4)', fontSize: 13 }}>No SKILL.md content available.</p>
            )}
          </div>
        </div>

        <aside className="skills-detail-side">
          <div className="skill-sh-meta">
            <div className="micro-mono">Installs</div>
            <div className="skill-sh-installs">{formatCompact(installCount)}</div>
          </div>
          <div className="skill-sh-meta">
            <div className="micro-mono">Repository</div>
            <div className="skill-sh-meta-value mono">{repo}</div>
          </div>
          {version && (
            <div className="skill-sh-meta">
              <div className="micro-mono">Version</div>
              <div className="skill-sh-meta-value mono">v{version}</div>
            </div>
          )}
          {formatDate(publishedAt) && (
            <div className="skill-sh-meta">
              <div className="micro-mono">First published</div>
              <div className="skill-sh-meta-value">{formatDate(publishedAt)}</div>
            </div>
          )}

          {deps.length > 0 && (
            <div className="card skills-side-card">
              <div className="micro-mono">Dependencies</div>
              <div className="skills-deps">
                {deps.map((dep, idx) => (
                  <div key={`${dep.type}-${dep.serviceSlug || dep.id || idx}`}>
                    <span className="tag">{dep.type}</span>
                    <strong>{dep.serviceSlug || dep.id || dep.endpointKey || 'dependency'}</strong>
                    {dep.purpose && <p>{dep.purpose}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {permissionEntries.length > 0 && (
            <div className="card skills-side-card">
              <div className="micro-mono">Permissions</div>
              <div className="skills-permissions">
                {permissionEntries.map(([key, label]) => <span className="tag" key={key}>{label}</span>)}
              </div>
            </div>
          )}

          {examples.length > 0 && (
            <div className="card skills-side-card">
              <div className="micro-mono">Examples</div>
              <div className="skills-examples">
                {examples.map((ex, i) => (
                  <div key={`${ex.title || 'example'}-${i}`}>
                    <strong>{ex.title || `Example ${i + 1}`}</strong>
                    <p>{ex.prompt}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={() => navigate('skill-detail', { slug: linkedSkill.slug })}
          >
            Open in Skill Market
          </button>
        </aside>
      </section>
    </div>
  );
};

const ChangelogTab = ({ api }) => {
  const versions = api.versions || [];
  if (!versions.length) {
    return (
      <div style={{maxWidth: 720, padding: '40px 0', textAlign:'center', color:'var(--fg-4)', fontSize: 13}}>
        No changelog yet — this API has only one version.
      </div>
    );
  }
  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toISOString().slice(0, 10); } catch { return ''; }
  };
  return (
    <div className="col gap-3" style={{maxWidth: 720}}>
      {versions.map(v => (
        <div key={v.id || v.version} className="card" style={{padding: 18}}>
          <div className="row between">
            <div className="row gap-2">
              <span className="mono" style={{fontSize: 14}}>{v.version}</span>
              {v.isActive && <span className="tag" style={{color:'var(--ok)'}}><span className="tag-dot ok"/> active</span>}
              {v.status === 'DEPRECATED' && <span className="tag" style={{color:'var(--fg-4)'}}>deprecated</span>}
              {v.status === 'DRAFT' && <span className="tag" style={{color:'var(--warn)'}}>draft</span>}
            </div>
            <span className="micro-mono">{fmtDate(v.createdAt)}</span>
          </div>
          {v.description ? (
            <div
              className="skills-md"
              style={{marginTop: 10, fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.7}}
              dangerouslySetInnerHTML={{ __html: renderSkillMarkdown(v.description) }}
            />
          ) : (
            <div style={{marginTop: 10, fontSize: 13, color: 'var(--fg-4)'}}>
              No release notes for this version.
            </div>
          )}
          {v._count?.endpoints != null && (
            <div className="micro-mono" style={{marginTop: 12, color:'var(--fg-4)'}}>
              {v._count.endpoints} endpoint{v._count.endpoints === 1 ? '' : 's'}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── API health ──────────────────────────────────────────────────────────
// Collapsed by default: a compact pill on the right of the hero metrics row
// (~status dot + score + status text). Click → opens a dialog (PC) or
// bottom-sheet (mobile) with the full 24h + 30d breakdown.

const HEALTH_COLORS = {
  healthy:   'var(--ok)',
  available: 'oklch(0.72 0.14 230)',
  degraded:  'var(--warn)',
  down:      'var(--err)',
  // --line-2 (#3e3e46) instead of --bg-3 (#1a1a1e). The darker shade was the
  // panel background, so empty buckets were almost invisible in tiny sparklines
  // (HealthPill 2px-wide cells, per-endpoint rows). --line-2 reads clearly as
  // "neutral grey block" against any of the page backgrounds.
  unknown:   'var(--line-2)',
};

function formatLatency(ms) {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms/1000).toFixed(2)}s`;
  return `${(ms/60_000).toFixed(1)}min`;
}
function renderTooltip(slot, unit, t) {
  if (!slot || slot.total === 0) {
    return `${slot?.bucket || ''}\n${t('console.health.status.unknown')}`;
  }
  const rate = ((slot.success / slot.total) * 100).toFixed(1);
  return `${slot.bucket}\n${slot.success}/${slot.total} success (${rate}%)${slot.avgLatencyMs ? ` · ${formatLatency(slot.avgLatencyMs)}` : ''}`;
}

// Compact entry-point. Inline 24h sparkline gives an at-a-glance read of
// connectivity over the last day without needing to open the dialog.
const HealthPill = ({ data, onClick, t }) => {
  const score = data?.score24h;
  const status = data?.currentStatus || 'healthy';
  const color = HEALTH_COLORS[status] || HEALTH_COLORS.healthy;
  const hourly = data?.hourly || [];
  return (
    <button
      type="button"
      className="health-pill"
      onClick={onClick}
      title={t('console.health.title')}
      aria-label={t('console.health.title')}
      style={{ marginLeft: 0 }}>
      <span className="health-pill-dot" style={{ background: color }}/>
      <span className="health-pill-score mono">
        {score == null ? '—' : score}
        <span className="health-pill-score-sub">/100</span>
      </span>
      {hourly.length > 0 && (
        <span className="health-pill-spark" aria-hidden="true">
          {hourly.map((h, i) => (
            <span
              key={i}
              className="health-pill-spark-cell"
              style={{ background: HEALTH_COLORS[h.status] }}
            />
          ))}
        </span>
      )}
      <span className="health-pill-status" style={{ color }}>
        {t(`console.health.status.${status}`)}
      </span>
      <XIcons.IconChevronRight size={11}/>
    </button>
  );
};

// Detailed view — header + stat row + legend + 24h trend + 30d trend.
// Mirrors the rich reference design but uses the existing Dialog primitives
// (which already render as a bottom-sheet on mobile via `responsive`).
// Per-endpoint health list — virtual-scrolled to handle 100+ endpoints.
//
// Three tricks at play:
//   1. Server pages with cursor (limit=20 default); we trigger nextPage when
//      the viewport approaches the bottom of the already-loaded rows.
//   2. Each row is fixed-height (ROW_H), so only the rows intersecting the
//      visible window are mounted — DOM count stays ~12 regardless of total.
//   3. Sort change resets cursor and reloads page 1; the server caches each
//      (sort) variant separately so it's a fast Redis hit on repeat.
const ROW_H = 56;
const ENDPOINT_LIST_HEIGHT = 320;
const OVERSCAN = 4;

const EndpointHealthList = ({ apiServiceId, t, onOpenEndpoint }) => {
  const [items, setItems] = React.useState([]);
  const [cursor, setCursor] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [total, setTotal] = React.useState(null);
  const [sort, setSort] = React.useState('worst');
  const [scrollTop, setScrollTop] = React.useState(0);
  const scrollerRef = React.useRef(null);
  // Track in-flight to avoid duplicate fetches when scroll fires rapidly.
  const fetchingRef = React.useRef(false);

  const fetchPage = React.useCallback(async (resetCursor = false) => {
    if (fetchingRef.current) return;
    if (!resetCursor && !hasMore) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const res = await XApi.apiHealthService.endpoints(apiServiceId, {
        limit: 20,
        cursor: resetCursor ? null : cursor,
        sort,
      });
      setItems(prev => (resetCursor ? res.items : [...prev, ...res.items]));
      setCursor(res.nextCursor);
      setHasMore(!!res.nextCursor);
      if (res.total != null) setTotal(res.total);
    } catch {} finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [apiServiceId, cursor, sort, hasMore]);

  // Initial load + sort change → reset
  React.useEffect(() => {
    setItems([]); setCursor(null); setHasMore(true); setTotal(null);
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiServiceId, sort]);

  // Trigger next page when scrolled within 4 rows of the bottom of loaded items.
  const onScroll = (e) => {
    const top = e.currentTarget.scrollTop;
    setScrollTop(top);
    const bottomPx = items.length * ROW_H;
    const viewportBottom = top + ENDPOINT_LIST_HEIGHT;
    if (hasMore && !fetchingRef.current && viewportBottom > bottomPx - OVERSCAN * ROW_H) {
      fetchPage(false);
    }
  };

  // Compute the slice of rows actually mounted in the DOM.
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const endIdx = Math.min(items.length, Math.ceil((scrollTop + ENDPOINT_LIST_HEIGHT) / ROW_H) + OVERSCAN);
  const visible = items.slice(startIdx, endIdx);
  const padTop = startIdx * ROW_H;
  const padBottom = Math.max(0, (items.length - endIdx) * ROW_H);

  return (
    <div className="health-dialog-section">
      <div className="row between" style={{ marginBottom: 8, alignItems:'center' }}>
        <div className="micro health-dialog-section-title" style={{ marginBottom: 0 }}>
          {t('console.health.endpoints.title')} {total != null && `· ${total}`}
        </div>
        <div className="row gap-1" style={{ alignItems:'center' }}>
          {[
            { k: 'worst', l: t('console.health.endpoints.sort.worst') },
            { k: 'calls', l: t('console.health.endpoints.sort.calls') },
            { k: 'path',  l: t('console.health.endpoints.sort.path') },
          ].map(s => (
            <button key={s.k} type="button"
              className="btn btn-sm"
              onClick={() => setSort(s.k)}
              style={{
                border: 'none', fontSize: 11, padding: '3px 8px',
                background: sort === s.k ? 'var(--bg-3)' : 'transparent',
                color: sort === s.k ? 'var(--fg-1)' : 'var(--fg-3)',
              }}>
              {s.l}
            </button>
          ))}
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="health-endpoints-scroll"
        onScroll={onScroll}
        style={{ height: ENDPOINT_LIST_HEIGHT }}>
        {items.length === 0 && !loading && (
          <div style={{ padding: 32, textAlign:'center', color:'var(--fg-3)', fontSize: 12 }}>
            {t('console.health.endpoints.empty')}
          </div>
        )}
        <div style={{ paddingTop: padTop, paddingBottom: padBottom }}>
          {visible.map((r, i) => (
            <EndpointHealthRow
              key={r.endpointId}
              row={r}
              t={t}
              onClick={() => onOpenEndpoint && onOpenEndpoint(r)}
            />
          ))}
        </div>
        {loading && (
          <div style={{ padding: 12, textAlign:'center', color:'var(--fg-3)', fontSize: 11 }}>
            <span className="spinner" style={{ width: 11, height: 11 }}/> Loading…
          </div>
        )}
      </div>
    </div>
  );
};

// One row in the virtual list. Fixed height = ROW_H. Renders a 8-cell sparkline
// of the full 24h so the row reads like a mini version of the top trend strip.
const EndpointHealthRow = ({ row, t, onClick }) => {
  const color = HEALTH_COLORS[row.currentStatus] || HEALTH_COLORS.healthy;
  // Show all 24 hourly buckets (was 8). Width-wise still fits comfortably between
  // the path and the score on PC; hidden on mobile via @media query.
  const hourly = row.hourly || [];
  return (
    <button
      type="button"
      className="health-endpoint-row"
      onClick={onClick}
      style={{ height: ROW_H }}>
      <span className="health-endpoint-dot" style={{ background: color }}/>
      <span className="health-endpoint-meta">
        <span className="health-endpoint-method mono">{row.method}</span>
        <span className="health-endpoint-path mono">{row.path}</span>
      </span>
      <span className="health-endpoint-spark" aria-hidden="true">
        {hourly.map((slot, i) => (
          <span key={i} className="health-endpoint-spark-cell"
            data-status={slot.status}
            style={{ background: HEALTH_COLORS[slot.status] }}/>
        ))}
      </span>
      <span className="health-endpoint-score mono" style={{ color }}>
        {row.score24h}
        <span className="health-endpoint-score-sub">/100</span>
      </span>
      <span className="health-endpoint-calls mono">
        {row.totalCalls24h > 0 ? `${row.totalCalls24h.toLocaleString()} calls` : '—'}
      </span>
    </button>
  );
};

const HealthDialog = ({ open, onClose, data, onRefresh, apiServiceId, t }) => {
  const score = data?.score24h;
  const status = data?.currentStatus || 'healthy';
  const color = HEALTH_COLORS[status] || HEALTH_COLORS.healthy;
  return (
    <Dialog open={open} onClose={onClose} maxWidth={720} responsive>
      <DialogHeader title={t('console.health.title')} onClose={onClose}/>
      <DialogBody padding={22}>
        {/* Stat row: score · avg latency · last check · refresh · status pill */}
        <div className="health-dialog-statrow">
          <div className="health-dialog-stat">
            <span className="micro">{t('console.health.score24h')}</span>
            <div className="mono" style={{ fontSize: 16, marginTop: 2, color }}>
              {score == null ? '—' : score}
              <span className="micro-mono" style={{ textTransform:'none', letterSpacing:0, fontSize: 10, color:'var(--fg-4)', marginLeft: 3 }}>/100</span>
            </div>
          </div>
          <div className="health-dialog-stat">
            <span className="micro">{t('console.health.avgLatency24h')}</span>
            <div className="mono" style={{ fontSize: 16, marginTop: 2 }}>
              {data?.avgLatencyMs != null ? formatLatency(data.avgLatencyMs) : '—'}
            </div>
          </div>
          <div className="health-dialog-stat">
            <span className="micro">{t('console.health.lastCheck')}</span>
            <div className="mono" style={{ fontSize: 13, marginTop: 4 }}>
              {data?.lastCheckAt || '—'}
            </div>
          </div>
          <button
            className="btn btn-sm btn-ghost health-dialog-refresh"
            onClick={onRefresh}
            type="button">
            <XIcons.IconRefresh size={12}/>
            <span>{t('console.health.refresh')}</span>
          </button>
          <span className="health-dialog-status-pill" style={{ borderColor: color, color }}>
            {t(`console.health.status.${status}`)}
          </span>
        </div>

        {/* Legend */}
        <div className="health-dialog-legend">
          {['healthy','available','degraded','down','unknown'].map(s => (
            <span key={s} className="health-dialog-legend-item">
              <span className="health-dialog-legend-dot" style={{ background: HEALTH_COLORS[s] }}/>
              {t(`console.health.status.${s}`)}
            </span>
          ))}
        </div>

        {/* 24h trend */}
        <div className="health-dialog-section">
          <div className="micro health-dialog-section-title">{t('console.health.trend24h')}</div>
          <div className="health-spark health-spark-24">
            {(data?.hourly || Array.from({length: 24})).map((slot, i) => (
              <span
                key={`h${i}`}
                className="health-spark-cell"
                title={slot ? renderTooltip(slot, 'hour', t) : ''}
                style={{ background: slot ? HEALTH_COLORS[slot.status] : 'var(--bg-3)' }}
              />
            ))}
          </div>
        </div>

        {/* 30d trend */}
        <div className="health-dialog-section">
          <div className="micro health-dialog-section-title">{t('console.health.trend30d')}</div>
          <div className="health-spark health-spark-30">
            {(data?.daily || Array.from({length: 30})).map((slot, i) => (
              <span
                key={`d${i}`}
                className="health-spark-cell"
                title={slot ? renderTooltip(slot, 'day', t) : ''}
                style={{ background: slot ? HEALTH_COLORS[slot.status] : 'var(--bg-3)' }}
              />
            ))}
          </div>
        </div>

        {/* Per-endpoint list — only loaded once the dialog is open. */}
        {apiServiceId && open && (
          <EndpointHealthList apiServiceId={apiServiceId} t={t}/>
        )}
      </DialogBody>
    </Dialog>
  );
};

const ApiDetailPage = () => {
  const { route, navigate } = XShell.useRoute();
  // Mock data is kept solely to populate the layout skeleton (e.g. seed an
  // endpoint shape so the sidebar and Try-It panel can render structurally
  // while we wait for the real fetch). The user-visible *values* — name,
  // description, stats, endpoint metadata — are gated behind `loadingApi`
  // and rendered as <Skel> blocks until the backend response lands. That
  // way the page never flashes the wrong API's data before the right one
  // loads.
  const mockApi = XData.APIS.find(a => a.id === route.params.id) || XData.APIS[0];
  const [api, setApi] = udS(mockApi);
  const [selEp, setSelEp] = udS(mockApi.endpoints[0]);
  // True from initial mount until `Promise.allSettled` resolves the fetch.
  // All header fields, stats, and endpoint chips render as skeletons while
  // this is true.
  const [loadingApi, setLoadingApi] = udS(true);
  // One-shot page entrance — flipped on the first frame after mount so the
  // header zones (.apx-stagger children) rise in with a short cascade instead
  // of popping. Never re-triggers; tab/endpoint swaps use .apx-fade instead.
  const [entered, setEntered] = udS(false);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const [tab, setTab] = udS('try');
  const [lang, setLang] = udS('curl');
  const [fav, setFav] = udS(false);
  const [subscribed, setSubscribed] = udS(false);
  // Health: a compact right-aligned pill on the hero shows status + score.
  // Click → modal (PC) / bottom-sheet (mobile) with the full breakdown.
  // Polled every 5min; matches server-side Redis TTL on /api-services/:id/health.
  const [healthData, setHealthData] = udS(null);
  const [healthOpen, setHealthOpen] = udS(false);
  const refreshHealth = React.useCallback(async () => {
    if (!route.params.id) return;
    try {
      const d = await XApi.apiHealthService.get(route.params.id);
      setHealthData(d);
    } catch {}
  }, [route.params.id]);
  const [reqBody, setReqBody] = udS(
    mockApi.id === 'llm-gateway'
      ? JSON.stringify({ model: 'claude-haiku-4.5', messages: [{role:'user', content:'Hello'}], stream: true }, null, 2)
      : '{\n  \n}'
  );
  // multipart/form-data | x-www-form-urlencoded 字段 [{key,type,value,file}]
  const [formFields, setFormFields] = udS([]);
  const [resp, setResp] = udS(null);
  const [streamText, setStreamText] = udS('');
  const [loading, setLoading] = udS(false);
  const [jsonModal, setJsonModal] = udS(null); // { title, value } or null — drives full-screen JSON viewer
  const [pathParams, setPathParams] = udS({});
  const [queryParams, setQueryParams] = udS([]);   // [{k, v}]
  const [extraHeaders, setExtraHeaders] = udS([]); // [{k, v}]
  // Endpoint sidebar filter: kicks in when an API has more than ~8 endpoints (rendered list
  // would overflow the viewport otherwise). Below the threshold the search box is hidden so
  // small APIs stay clean.
  const [epQuery, setEpQuery] = udS('');
  // 分组折叠状态(键 = 分组名,未分组用 '__ungrouped__');参考注册页文件夹式分组。
  const [collapsedGroups, setCollapsedGroups] = udS({});
  const toggleGroup = (key) => setCollapsedGroups((c) => ({ ...c, [key]: !c[key] }));
  // Owner-only "hide hidden endpoints" toggle. Default = false so the
  // creator lands on the full picture; switching ON gives them the
  // consumer's-eye view of their public surface without ever leaving
  // the page. Non-owners never see this toggle (no hidden data reaches
  // them anyway — backend's safeEndpoints filters before the wire).
  const [hideHiddenEps, setHideHiddenEps] = udS(false);
  // Mobile-only: endpoint picker is a bottom drawer instead of the sticky tall
  // sidebar, which would dominate the viewport on phones.
  const [epPickerOpen, setEpPickerOpen] = udS(false);
  // Async-mode toggle for OPTIONAL endpoints. REQUIRED endpoints force this on.
  const [asyncOptIn, setAsyncOptIn] = udS(false);
  // Active async task being polled. { taskId, status, expiresAt, lastPoll, result, error }.
  const [asyncTask, setAsyncTask] = udS(null);
  // Increments once per second while a task is pending/processing so the UI can
  // show a live elapsed counter without coupling the redraw cadence to polling.
  const [asyncTick, setAsyncTick] = udS(0);
  const asyncPollRef = React.useRef(null);
  // 二进制响应(audio/image/video/pdf/…)预览:invoke() 用 res.blob() 接收非文本响应。
  // 这里为 Blob body 造一个 objectURL 供 TaskFileRenderer 内联播放/显示/下载,换响应或
  // 组件卸载时回收旧 URL 防内存泄漏(IIFE 渲染段里不能用 hooks,故提到组件顶层)。
  const [respBlobUrl, setRespBlobUrl] = udS(null);
  React.useEffect(() => {
    const b = resp && !resp.streaming && resp.body instanceof Blob ? resp.body : null;
    if (!b) { setRespBlobUrl(null); return; }
    const u = URL.createObjectURL(b);
    setRespBlobUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [resp]);
  const { t: tDetail } = useT();
  React.useEffect(() => {
    if (!epPickerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [epPickerOpen]);
  // Owner check — drives the toggle visibility. Use raw provider id from
  // api._raw (preserved by the merger above) against the auth user's
  // backend id. We also fall back to a structural signal (any endpoint
  // arrived with hidden=true) since backend's safeEndpoints means only
  // owners ever see hidden — if we have hidden data, we're definitely
  // the owner even if the auth context hasn't hydrated yet.
  const { user: _authUser, authed: _authed } = XShell.useAuth();
  const isOwner =
    !!(api && api._raw && _authUser?._raw?.id && api._raw.providerId === _authUser._raw.id) ||
    (Array.isArray(api?.endpoints) && api.endpoints.some((e) => e.hidden));

  const filteredEndpoints = udM(() => {
    if (!api || !Array.isArray(api.endpoints)) return [];
    let base = api.endpoints;
    // Owner-only: if the "hide hidden" switch is on, drop hidden eps.
    // For non-owners base never contains hidden, so this is a no-op there.
    if (hideHiddenEps) base = base.filter((ep) => !ep.hidden);
    const q = epQuery.trim().toLowerCase();
    const list = q
      ? base.filter((ep) =>
          `${ep.name || ''} ${ep.path || ''} ${ep.method || ''} ${ep.group || ''}`
            .toLowerCase()
            .includes(q),
        )
      : base;
    // 按 group 聚类（保持首次出现顺序，未分组排末尾），详情列表据此按组分隔展示
    const order = [];
    const gmap = new Map();
    for (const ep of list) {
      const g = (ep.group || '').trim();
      if (!gmap.has(g)) { gmap.set(g, []); order.push(g); }
      gmap.get(g).push(ep);
    }
    order.sort((a, b) => (a === '' ? 1 : 0) - (b === '' ? 1 : 0));
    return order.flatMap((g) => gmap.get(g));
  }, [api, epQuery, hideHiddenEps]);
  const abortRef = React.useRef(null);
  // WS adapter metadata (GET /ws/adapters) — only fetched when the service has a WEBSOCKET
  // endpoint, drives the adapter chip / clientProtocol hint in WsTryItPanel. Empty on error.
  const [wsAdapters, setWsAdapters] = udS([]);
  const hasWsEndpoint = udM(
    () => Array.isArray(api?.endpoints) && api.endpoints.some((ep) => ep.protocol === 'WEBSOCKET'),
    [api],
  );
  React.useEffect(() => {
    if (!hasWsEndpoint) return;
    let cancelled = false;
    XApi.apiSvc.getWsAdapters()
      .then((list) => { if (!cancelled) setWsAdapters(list || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [hasWsEndpoint]);
  const isWs = selEp && selEp.protocol === 'WEBSOCKET';
  const envVars = XEnv.useEnv();
  const apiKeyVar = envVars.find(v => v.name === 'XAPI_KEY');
  const hasKey = !!(apiKeyVar && apiKeyVar.value);
  const keyValue = hasKey ? apiKeyVar.value : '';
  const keyForDisplay = hasKey ? (keyValue.slice(0, 8) + '…' + keyValue.slice(-4)) : '${XAPI_KEY}';
  const keyForCode = hasKey ? keyValue : '${XAPI_KEY}';

  const pathParamNames = udM(() => {
    const out = new Set();
    const p = (selEp && selEp.path) || '';
    let m;
    const re1 = /:([A-Za-z_][A-Za-z0-9_]*)/g;
    while ((m = re1.exec(p))) out.add(m[1]);
    const re2 = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
    while ((m = re2.exec(p))) out.add(m[1]);
    return [...out];
  }, [selEp]);

  // Reset and pre-populate endpoint-scoped inputs when switching endpoints.
  // Backend stores params/headers/pathParams as object maps:
  //   { name: { schema: {type, ...}, example, required, description } }
  // We seed each row with the configured `example` so the user has a working starting point
  // instead of an empty form they have to retype from the docs.
  React.useEffect(() => {
    const raw = selEp && selEp._raw;
    const objToRows = (obj) => {
      if (!obj || typeof obj !== 'object') return [];
      // `locked: true` flags rows that came from the registered API contract — key field
      // becomes read-only and the remove button hides. The value stays editable so the user
      // can test with their own data. User-added rows (via the + button) get locked: false.
      return Object.entries(obj).map(([name, def]) => ({
        k: name,
        v: def && def.example != null ? String(def.example) : '',
        locked: true,
      }));
    };
    const objToValueMap = (obj) => {
      if (!obj || typeof obj !== 'object') return {};
      const m = {};
      Object.entries(obj).forEach(([name, def]) => {
        m[name] = def && def.example != null ? String(def.example) : '';
      });
      return m;
    };
    setPathParams(objToValueMap(raw?.pathParams));
    setQueryParams(objToRows(raw?.params));
    setExtraHeaders(objToRows(raw?.headers));
    // 表单字段（form-data / urlencoded）：从 bodySchema.fields 种子，file 字段配文件选择器
    const ffields = (raw && raw.bodySchema && Array.isArray(raw.bodySchema.fields)) ? raw.bodySchema.fields : null;
    setFormFields(ffields ? ffields.map((f) => ({ key: f.key || '', type: f.type === 'file' ? 'file' : 'text', value: f.value || '', file: null })) : []);
    // Body example: prefer bodySchema.example, fall back to mock seed for llm-gateway, otherwise empty json.
    if (raw && raw.bodySchema && raw.bodySchema.example != null) {
      const ex = raw.bodySchema.example;
      setReqBody(typeof ex === 'string' ? ex : JSON.stringify(ex, null, 2));
    } else if (api?.id === 'llm-gateway') {
      setReqBody(JSON.stringify({ model: 'claude-haiku-4.5', messages: [{ role: 'user', content: 'Hello' }], stream: true }, null, 2));
    } else {
      setReqBody('{\n  \n}');
    }
    setResp(null); setStreamText('');
  }, [selEp && selEp.id]);

  // Real data sources:
  //   /api-services/:id                     → full service record (endpoints, auth, pricing)
  //   /statistics/api/:apiId/public         → uptime, avgLatency, totalHits over 30d
  //   /api-services/:id/favorite (JWT)      → is-favorited; POST/DELETE to toggle
  //   /api-services/:id/health              → 24h/30d health (server-cached 5min)
  const apiId = route.params.id;

  // Poll health every 5min — server caches the same window, so this just
  // re-pulls the cached blob.
  React.useEffect(() => {
    if (!apiId) return;
    let cancelled = false;
    const pull = () => XApi.apiHealthService.get(apiId)
      .then(d => { if (!cancelled) setHealthData(d); })
      .catch(() => {});
    pull();
    const id = setInterval(pull, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [apiId]);
  // Admin-review mode: when navigated here from the admin review page with
  // asAdmin=true in the URL, we ask the backend to bypass the non-ACTIVE
  // visibility wall AND render a banner so the admin clearly knows they're
  // looking at a not-yet-public service.
  const asAdmin = route.params.asAdmin === 'true';
  React.useEffect(() => {
    if (!apiId) return;
    let cancelled = false;
    setLoadingApi(true);
    (async () => {
      const [svcR, statsR, verR] = await Promise.allSettled([
        XApi.apiSvc.get(apiId, { asAdmin }),
        XApi.statsService.apiPublic(apiId, 30),
        XApi.apiSvc.listVersions(apiId),
      ]);
      if (cancelled) return;

      const a = svcR.status === 'fulfilled' ? svcR.value : null;
      const s = statsR.status === 'fulfilled' ? statsR.value : null;
      const versionsRes = verR.status === 'fulfilled' ? verR.value : null;
      // findOne returns isFavorited/isSubscribed only when the request was authenticated.
      if (a && typeof a.isFavorited === 'boolean') setFav(a.isFavorited);
      if (a && typeof a.isSubscribed === 'boolean') setSubscribed(a.isSubscribed);
      if (!a) { setLoadingApi(false); return; }

      // Backend findOne shape (api.service.ts:1612): { ...api, currentVersion: { ...versionPublic, endpoints, baseUrl, authType, ... } }
      // Old shape (legacy or fallback): { ...api, endpoints }
      const realEndpoints =
        (Array.isArray(a.currentVersion?.endpoints) && a.currentVersion.endpoints) ||
        (Array.isArray(a.endpoints) && a.endpoints) ||
        [];
      const merged = {
        ...mockApi,
        id: a.id,
        name: a.name || mockApi.name,
        host: a.host || mockApi.host,
        description: a.description || mockApi.description,
        website: a.website || '',
        aboutMarkdown: a.aboutMarkdown || '',
        linkedSkill: a.linkedSkill || null,
        logoUrl: a.logoUrl || '',
        category: a.category || mockApi.category,
        provider: a.provider || mockApi.provider,
        baseUrl: a.currentVersion?.baseUrl || a.baseUrl || mockApi.baseUrl,
        endpoints: realEndpoints.length
          ? realEndpoints.map(ep => {
              // 拆出 {{env}} 前缀:消费者看到/调用的是干净的 client path(如 /v1/x);
              // env 只是内部路由(网关据此选上游 baseUrl),不该出现在路径/curl/示例里。
              const _em = (ep.path || '').match(/^\{\{([A-Za-z0-9_-]+)\}\}([\s\S]*)$/);
              const epEnv = _em ? _em[1] : null;
              const epPath = _em ? _em[2] : (ep.path || '');
              const billingType = ep.billingType || 'PER_CALL';
              // Per-billing-mode display strings. Crucially: PER_TOKEN endpoints often have a
              // non-zero `costPerCall` left over as a default, but that field is meaningless for
              // token-priced endpoints — never surface it as $X.
              const cost = ep.protocol === 'WEBSOCKET' && !isFreeEndpoint(ep)
                ? wsListCost({ _raw: ep })
                : endpointListCost(ep);
              return {
                id: ep.id || ep.method + ' ' + epPath,
                method: ep.method, path: epPath,
                name: ep.name || epPath,
                group: ep.group || null,   // 分组折叠用(之前被漏掉,导致分组不展示)
                env: epEnv,                // 内部上游环境(default=null);仅 owner 可选展示
                cost,
                billingType,
                costPerCall: ep.costPerCall,
                costPerResource: ep.costPerResource,
                resourceCountJsonPath: ep.resourceCountJsonPath || null,
                estimatedMaxResources: ep.estimatedMaxResources ?? null,
                estimatedMaxTokens: ep.estimatedMaxTokens ?? null,
                tokenPricing: ep.tokenPricing,
                modelQuotes: ep.modelQuotes ?? null,
                dynamicRules: ep.dynamicRules || null,
                supportsStreaming: !!ep.supportsStreaming,
                asyncMode: ep.asyncMode || 'NONE',
                // WebSocket protocol: drives the Try-It panel switch (HTTP fetch vs WS view).
                // wsConfig / wsBilling ride along on _raw (see WsTryItPanel / WsConsole).
                protocol: ep.protocol || 'HTTP',
                desc: ep.description || '',
                // Preserve hidden flag so owner-side rendering can dim
                // these and mark with eye-off icon. Backend filters out
                // hidden for non-owner viewers (findOne#safeEndpoints),
                // so this is always false for consumers.
                hidden: !!ep.hidden,
                aiInterfaceSpecId: ep.aiInterfaceSpecId || null,
                // 对外展示「该接口支持的模型」：取 aiModelMappings 的原始 modelName。手动声明的
                // 'other' 及 canonical 列表外的模型 canonicalId 为 null，但 modelName 照常保留展示。
                servedModels: Array.isArray(ep.aiModelMappings)
                  ? [...new Set(ep.aiModelMappings.map(m => m && m.modelName).filter(Boolean))]
                  : [],
                _raw: ep,
              };
            })
          : [],
        status: (a.status || '').toLowerCase(),
        verified: a.isPublic,
        // Fill stats from /statistics/api/:id/public — otherwise keep whatever the service payload has (usually 0).
        hits: s && s.totalHits != null ? Number(s.totalHits) : (a.calls30d || 0),
        uptime: s && s.uptime != null ? Number(s.uptime) : (a.uptime || mockApi.uptime),
        latency: s && s.avgLatency != null ? Math.round(Number(s.avgLatency)) : (a.latency || mockApi.latency),
        stars: typeof a.favoriteCount === 'number' ? a.favoriteCount : (a.favoriteCount || 0),
        // currentVersion (with id, version, status) for Documentation tab + version dropdown
        version: a.currentVersion?.version || a.activeVersion?.version || '',
        authType: a.currentVersion?.authType || '',
        // Full version history powering the Changelog tab. Backend shape: { totalMajorVersions, activeVersionId, versions: [{ id, version, status, createdAt, updatedAt, description, _count: { endpoints }, isActive, ... }] }
        versions: Array.isArray(versionsRes?.versions) ? versionsRes.versions : [],
        _raw: a,
        _stats: s,
      };
      setApi(merged);
      // Default to first visible endpoint so owner viewing own API
      // doesn't land on a hidden one. Falls back to [0] if all hidden.
      const firstVisible = merged.endpoints.find((e) => !e.hidden) || merged.endpoints[0];
      setSelEp(firstVisible);
      setLoadingApi(false);
    })();
    return () => { cancelled = true; };
  }, [apiId]);

  // Toggle favorite on the backend. Optimistic — bump the displayed count so the badge
  // moves the moment you click, then roll back if the request fails.
  const toggleFavorite = async () => {
    if (!XApi.tokens.getAccess()) { navigate('login'); return; }
    const next = !fav;
    const delta = next ? 1 : -1;
    setFav(next);
    setApi(a => ({ ...a, stars: Math.max(0, (a.stars || 0) + delta) }));
    try {
      if (next) await XApi.favoriteService.add(api.id);
      else await XApi.favoriteService.remove(api.id);
    } catch {
      setFav(!next);
      setApi(a => ({ ...a, stars: Math.max(0, (a.stars || 0) - delta) }));
    }
  };

  // 订阅更新通知——独立于收藏。订阅后该 API 发布带 changelog 的新版本会推邮件（需已验证邮箱）。
  const toggleSubscribe = async () => {
    if (!XApi.tokens.getAccess()) { navigate('login'); return; }
    const next = !subscribed;
    setSubscribed(next);
    try {
      if (next) await XApi.subscriptionService.add(api.id);
      else await XApi.subscriptionService.remove(api.id);
    } catch {
      setSubscribed(!next);
    }
  };

  const parseBody = () => {
    if (!reqBody.trim()) return undefined;
    try { return JSON.parse(reqBody); }
    catch { return reqBody; }
  };

  const queryObj = () => Object.fromEntries(queryParams.filter(p => p && p.k).map(p => [p.k, XEnv.resolve(p.v || '')]));
  const headerObj = () => {
    const h = { 'Content-Type': 'application/json' };
    extraHeaders.forEach(p => { if (p && p.k) h[p.k] = XEnv.resolve(p.v || ''); });
    return h;
  };
  const resolvedPath = () => {
    let p = (selEp && selEp.path) || '/';
    pathParamNames.forEach(n => {
      const v = encodeURIComponent(XEnv.resolve(pathParams[n] || ''));
      p = p.replace(new RegExp(':' + n + '(?=/|$)', 'g'), v).replace(new RegExp('\\{' + n + '\\}', 'g'), v);
    });
    return p;
  };

  // Whether this endpoint should be invoked as an async task.
  // REQUIRED → always; OPTIONAL → user toggle; NONE → never.
  const asyncMode = (selEp && selEp.asyncMode) || 'NONE';
  const useAsync = asyncMode === 'REQUIRED' || (asyncMode === 'OPTIONAL' && asyncOptIn);

  // Stop the current async polling loop. Safe to call when nothing is running.
  const stopAsyncPolling = () => {
    if (asyncPollRef.current) {
      clearTimeout(asyncPollRef.current);
      asyncPollRef.current = null;
    }
  };

  // Reset polling when the user navigates between endpoints. Otherwise a stale task from
  // endpoint A would keep firing while the user has switched to endpoint B.
  React.useEffect(() => {
    stopAsyncPolling();
    setAsyncTask(null);
    setResp(null);
  }, [selEp && selEp.id]);

  React.useEffect(() => {
    const active = asyncTask && (asyncTask.status === 'pending' || asyncTask.status === 'processing');
    if (!active) return;
    const id = setInterval(() => setAsyncTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [asyncTask && asyncTask.status]);

  // Decide the next poll delay (ms). Priority:
  //   1) Backend Retry-After hint (parsed by taskService.poll into data._retryAfter, seconds)
  //   2) Tiered fallback based on attempt count — keeps QPS low for slow tasks like
  //      video generation that routinely take 60-120s:
  //        attempts < 10  →  2s   (fast initial — task often completes quickly)
  //        attempts < 30  →  5s   (medium horizon)
  //        attempts >= 30 → 10s   (long tail)
  const pickNextDelayMs = (data, attempts) => {
    const hinted = data && data._retryAfter;
    if (hinted) return hinted * 1000;
    if (attempts < 10) return 2000;
    if (attempts < 30) return 5000;
    return 10000;
  };
  const schedulePoll = (taskId, attempts = 0) => {
    const delay = attempts === 0
      ? 2000
      : pickNextDelayMs(asyncTask, attempts);
    asyncPollRef.current = setTimeout(async () => {
      try {
        const data = await XApi.taskService.poll(api.host, taskId, keyValue);
        if (!data) {
          setAsyncTask(prev => prev && prev.taskId === taskId ? { ...prev, status: 'failed', error: { code: 'task_not_found', message: 'Task not found' } } : prev);
          return;
        }
        // status comes back lowercased. Pending/processing → keep polling.
        const finalized = ['succeeded', 'failed', 'expired'].includes(data.status);
        setAsyncTask(prev => prev && prev.taskId === taskId ? {
          ...prev,
          status: data.status,
          result: data.result,
          error: data.error,
          createdAt: data.created_at,
          expiresAt: data.expires_at,
          lastPoll: Date.now(),
          attempts: attempts + 1,
        } : prev);
        if (!finalized) {
          // Backend Retry-After header overrides our tier — re-pick on each iteration.
          const nextDelay = pickNextDelayMs(data, attempts + 1);
          asyncPollRef.current = setTimeout(() => schedulePoll(taskId, attempts + 1), nextDelay);
        } else {
          asyncPollRef.current = null;
        }
      } catch (e) {
        // Network blip — retry once after 4s. After that bail and surface error.
        setAsyncTask(prev => prev && prev.taskId === taskId ? { ...prev, lastError: e.message || 'Poll failed' } : prev);
        asyncPollRef.current = setTimeout(() => schedulePoll(taskId, attempts + 1), 4000);
      }
    }, delay);
  };

  const bodyCt = (selEp && selEp._raw && selEp._raw.bodySchema) ? selEp._raw.bodySchema.contentType : undefined;
  const isMultipart = bodyCt === 'multipart/form-data';
  const isFormUrlencoded = bodyCt === 'application/x-www-form-urlencoded';
  const isFormBody = isMultipart || isFormUrlencoded;

  const send = async () => {
    if (!hasKey) return;
    stopAsyncPolling();
    setAsyncTask(null);
    setLoading(true); setResp(null); setStreamText('');
    let body = parseBody();
    if (isFormBody) {
      if (isMultipart) {
        const fd = new FormData();
        for (const f of formFields) {
          if (!f.key) continue;
          if (f.type === 'file') { if (f.file) fd.append(f.key, f.file, f.file.name); }
          else fd.append(f.key, XEnv.resolve(f.value || ''));
        }
        body = fd; // gateway.invoke 识别 FormData → 浏览器带 boundary 发 multipart
      } else {
        const usp = new URLSearchParams();
        for (const f of formFields) { if (f.key) usp.append(f.key, XEnv.resolve(f.value || '')); }
        body = usp.toString();
      }
    }
    const isStream = !useAsync && body && typeof body === 'object' && body.stream === true;
    abortRef.current = new AbortController();
    const resolvedPathParams = {};
    Object.keys(pathParams || {}).forEach(k => { resolvedPathParams[k] = XEnv.resolve(pathParams[k] || ''); });
    const payload = {
      host: api.host,
      method: selEp.method,
      path: selEp.path,
      pathParams: resolvedPathParams,
      queryParams: queryObj(),
      apiKey: keyValue,
      headers: (() => { const sh = headerObj(); if (isFormUrlencoded) sh['Content-Type'] = 'application/x-www-form-urlencoded'; return sh; })(),
      body: selEp.method === 'GET' || selEp.method === 'HEAD' ? undefined : body,
      streaming: isStream,
      signal: abortRef.current.signal,
      onChunk: isStream ? ((piece) => { if (piece) setStreamText(s => s + piece); }) : undefined,
      async: useAsync,
    };
    try {
      const r = await XApi.gateway.invoke(payload);
      // Async submission: 202 with task_id. Switch into polling mode and let the regular
      // resp panel stay empty — the async card below renders the live status.
      if (r.async && r.taskId) {
        const task = {
          taskId: r.taskId,
          status: r.taskStatus || 'pending',
          expiresAt: r.expiresAt,
          createdAt: new Date().toISOString(),
          submittedAt: Date.now(),
        };
        setAsyncTask(task);
        setResp({ status: 202, latency: r.latency, headers: r.headers || {}, body: r.body, async: true, taskId: r.taskId });
        schedulePoll(r.taskId);
      } else {
        setResp({
          status: r.status || (r.ok ? 200 : 0),
          latency: r.latency,
          headers: r.headers || {},
          body: r.body,
          streaming: r.streaming,
          error: r.error,
        });
      }
    } catch (e) {
      if (e && (e.name === 'AbortError' || /aborted/i.test(e.message || ''))) {
        setResp({ status: 0, latency: 0, body: null, error: 'Aborted' });
      } else {
        setResp({ status: 0, latency: 0, body: null, error: e.message || 'Request failed' });
      }
    } finally { setLoading(false); abortRef.current = null; }
  };

  const abort = () => {
    try { abortRef.current && abortRef.current.abort(); } catch {}
    stopAsyncPolling();
  };

  // Tear down any running poll when the component unmounts.
  React.useEffect(() => () => stopAsyncPolling(), []);

  const addQuery = () => setQueryParams(p => p.concat([{ k:'', v:'', locked: false }]));
  const addHeader = () => setExtraHeaders(p => p.concat([{ k:'', v:'', locked: false }]));

  const gwUrl = XApi.helpers.buildGatewayUrl(api.host, api.integrationMode);
  const fullPath = (() => {
    const p = resolvedPath();
    const qs = Object.entries(queryObj()).filter(([,v]) => v !== '').map(([k,v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
    return p + (qs ? (p.includes('?') ? '&' : '?') + qs : '');
  })();
  const headerLines = Object.entries(headerObj()).map(([k,v]) => `  -H "${k}: ${v}"`).join(' \\\n');
  const asyncHeaderLine = useAsync ? '  -H "X-XAPI-Async: true" \\' : '';
  const curl = [
    `curl -X ${selEp?.method || 'GET'} ${gwUrl}${fullPath} \\`,
    `  -H "xapi-key: ${keyForCode}" \\`,
    asyncHeaderLine,
    headerLines + (selEp?.method !== 'GET' && selEp?.method !== 'HEAD' && reqBody.trim() ? ' \\' : ''),
    selEp?.method !== 'GET' && selEp?.method !== 'HEAD' && reqBody.trim() ? `  -d '${reqBody.replace(/\n\s*/g, ' ').replace(/'/g, "'\\''")}'` : '',
  ].filter(Boolean).join('\n');

  const codeJs = `import { xapi } from '@xapi/client';

// XAPI_KEY lives in your env — not committed, not shared.
const client = xapi({ apiKey: process.env.XAPI_KEY });

const res = await client.${api.slug}.${(selEp?.name || 'endpoint').toLowerCase().replace(/\s+/g,'_')}({
  // params
});`;

  const codePy = `from xapi import Client
import os

client = Client(api_key=os.environ["XAPI_KEY"])
res = client.${api.slug}.call("${selEp?.method || 'GET'} ${selEp?.path || '/'}", json={})
print(res)`;

  const codes = { curl, js: codeJs, python: codePy };

  // A service can legitimately have zero visible endpoints (misconfigured / mid-publish, or
  // all endpoints hidden from this viewer). Render a graceful empty state instead of letting
  // the endpoint-centric UI below dereference an undefined selEp and white-screen the page.
  // (Placed after all hooks + the null-safe code-sample computations above.)
  if (!loadingApi && !selEp) {
    return (
      <div style={{ padding: '80px 40px', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 15, marginBottom: 6 }}>此服务暂无可用端点</div>
        <div style={{ fontSize: 13, color: 'var(--fg-4)' }}>
          该 API 尚未发布可用接口，或所有端点对当前访客隐藏。
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Admin-review mode banner — only shown when admin opened this page from
          the review queue. Reminds them the service is not yet public, and
          provides a quick exit back to the review page. */}
      {asAdmin && (
        <div className="row between" style={{
          padding: '10px 40px',
          background: 'rgba(245, 158, 11, 0.08)',
          borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
          color: 'var(--warn)',
          fontSize: 13,
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
        }}>
          <div className="row gap-2" style={{ alignItems: 'center', minWidth: 0 }}>
            <XIcons.IconShield size={14}/>
            <span><strong>审核模式</strong> · 当前服务状态 {api?.status || '...'}，公开未上线。Try-It 调用会真实命中上游 — 请验证内容合规。</span>
          </div>
          <button className="btn btn-sm" onClick={() => navigate('admin')}>
            <XIcons.IconArrowLeft size={11}/> 返回审核队列
          </button>
        </div>
      )}

      {/* Header zones:
          A nav · B identity (logo/name/provider/host) · C metrics · D status+links · E blurb
          Category only once (tag in D). Long docs live in About tab. */}
      <div className="api-detail-header">
        <div className={`api-detail-header-inner apx-stagger${entered ? ' is-entered' : ''}`}>
          {/* A — navigation */}
          <nav className="api-detail-crumb" aria-label="Breadcrumb">
            <a onClick={() => navigate('market')} style={{cursor:'pointer'}}>Marketplace</a>
            <XIcons.IconChevronRight size={11}/>
            {loadingApi ? <Skel w={56} h={11}/> : <span>{api.category || 'API'}</span>}
            <XIcons.IconChevronRight size={11}/>
            {loadingApi ? <Skel w={90} h={11}/> : <span className="api-detail-crumb-current">{api.name}</span>}
          </nav>

          {/* B + C — identity | metrics */}
          <div className="api-detail-hero">
            <div className="api-detail-identity">
              <div className="api-detail-logo" aria-hidden="true">
                {loadingApi ? (
                  <Skel w={22} h={22} style={{borderRadius: 4}}/>
                ) : api.logoUrl ? (
                  <img src={api.logoUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <span>{api.name?.[0] || '?'}</span>
                )}
              </div>
              <div className="api-detail-identity-text">
                {loadingApi ? (
                  <Skel w={200} h={22}/>
                ) : (
                  <h1 className="api-detail-title">{api.name}</h1>
                )}
                <div className="api-detail-meta">
                  {loadingApi ? (
                    <Skel w={220} h={12}/>
                  ) : (
                    <>
                      {api.provider && <span className="api-detail-meta-provider">{api.provider}</span>}
                      {api.provider && api.host && <span className="api-detail-meta-sep" aria-hidden="true">·</span>}
                      {api.host && <span className="mono api-detail-meta-host">{api.host}</span>}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="api-detail-metrics" aria-label="Runtime metrics">
              {loadingApi ? (
                [1, 2, 3].map((i) => <Skel key={i} w={64} h={28} />)
              ) : (() => {
                const bill = headerBillingLabel(api.endpoints);
                return (
                  <>
                    <div className="api-detail-metric" title="P95 latency">
                      <span className="api-detail-metric-value mono">{api.latency}ms</span>
                      <span className="api-detail-metric-label">p95</span>
                    </div>
                    <div className="api-detail-metric-div" aria-hidden="true" />
                    <div className="api-detail-metric" title="Total calls">
                      <span className="api-detail-metric-value mono">{XPrim.fmt(api.hits)}</span>
                      <span className="api-detail-metric-label">calls</span>
                    </div>
                    <div className="api-detail-metric-div" aria-hidden="true" />
                    <div className="api-detail-metric" title={bill.tooltip || 'Billing model'}>
                      <span className="api-detail-metric-value mono">{bill.label}</span>
                    </div>
                    <div className="api-detail-metric-div" aria-hidden="true" />
                    <HealthPill
                      data={healthData}
                      onClick={() => setHealthOpen(true)}
                      t={tDetail}
                    />
                  </>
                );
              })()}
            </div>
          </div>

          {/* D — status chips + external links (category only here, not in meta) */}
          {!loadingApi && (
            <div className="api-detail-status-row">
              <div className="api-detail-chips">
                {api.verified && (
                  <span className="api-detail-chip api-detail-chip-ok">✓ Verified</span>
                )}
                {(() => {
                  const st = headerStatusChip(api.status);
                  return (
                    <span className={`api-detail-chip api-detail-chip-${st.tone}`}>
                      <span className={`api-detail-chip-dot tone-${st.tone}`} aria-hidden="true" />
                      {st.text}
                    </span>
                  );
                })()}
                {api.category && (
                  <span className="api-detail-chip api-detail-chip-muted">{api.category}</span>
                )}
              </div>
              <div className="api-detail-links">
                {safeWebsiteHref(api.website) && (
                  <a
                    className="api-detail-ext-link"
                    href={safeWebsiteHref(api.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Website ↗
                  </a>
                )}
                {api.linkedSkill?.slug && (
                  <button
                    type="button"
                    className="api-detail-ext-link"
                    onClick={() => setTab('skill')}
                    title={api.linkedSkill.name}
                  >
                    Skill ↗
                  </button>
                )}
                <button
                  type="button"
                  className="api-detail-ext-link"
                  onClick={toggleSubscribe}
                  title={subscribed ? '取消订阅更新通知' : '订阅更新通知——新版本发布时收邮件'}
                  aria-pressed={subscribed}
                >
                  {subscribed ? '已订阅更新' : '订阅更新'}
                </button>
              </div>
            </div>
          )}

          {/* E — short marketplace blurb only */}
          {loadingApi ? (
            <div className="api-detail-desc-skel"><Skel w="70%" h={13}/></div>
          ) : api.description ? (
            <p className="api-detail-desc" title={api.description}>{api.description}</p>
          ) : null}
        </div>
      </div>

      {/* Tabs */}
      <div className={`api-detail-tabs-wrap apx-enter${entered ? ' is-entered' : ''}`} style={{borderBottom:'1px solid var(--line)', padding: '0 40px', background:'var(--bg)', transitionDelay: '140ms'}}>
        <div style={{maxWidth: 1480, margin:'0 auto'}}>
          <div className="tabs">
            {[
              {k:'try', l:'Try it'},
              {k:'endpoints', l:'Endpoints'},
              {k:'about', l:'About'},
              {k:'skill', l:'Skill'},
              {k:'changelog', l:'Changelog'},
            ].map(t => (
              <div key={t.k} className={`tab ${tab===t.k?'active':''}`} onClick={()=>setTab(t.k)}>{t.l}</div>
            ))}
          </div>
        </div>
      </div>

      {/* Content — two-layer wrap (full-width outer + maxWidth-1480 inner)
          to match the Header and Tabs above. Single-wrapper makes the body
          content land 40px to the right of the tabs because of how max-width
          + auto-margin interacts with inner padding; the two-layer pattern
          keeps tabs and body content perfectly column-aligned on desktop. */}
      <div className={`api-detail-body apx-enter${entered ? ' is-entered' : ''}`} style={{padding: '28px 40px 80px', transitionDelay: '200ms'}}>
       {/* key={tab} remounts the panel on every tab switch so .apx-fade replays
           — a quick 200ms fade-rise that keeps swaps from feeling like a hard cut. */}
       <div key={tab} className="apx-fade" style={{maxWidth: 1480, margin: '0 auto'}}>
        {tab === 'try' && (
          <>
          {/* Mobile-only endpoint picker trigger — opens a bottom drawer with
              the same endpoint list. The desktop sticky-tall sidebar is hidden
              on phones via .endpoints-side CSS. */}
          <button
            className="btn btn-sm api-detail-ep-trigger"
            onClick={()=>setEpPickerOpen(true)}
            style={{ marginBottom: 14, width:'100%', justifyContent:'space-between' }}
          >
            <span className="row gap-2" style={{alignItems:'center', minWidth: 0}}>
              <XPrim.MethodChip method={selEp.method} />
              <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize: 12}}>{selEp.path}</span>
              <AsyncTag mode={selEp.asyncMode} />
            </span>
            <XIcons.IconChevronDown size={11}/>
          </button>
          <div className="api-try-grid" style={{display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: 16, alignItems: 'start'}}>
            {/* Endpoint list. Search box appears when the API has > 8 endpoints (small lists
                stay visually clean). The card has an explicit height (not fit-content) so the
                inner flex:1 list always has a bounded scroll area — otherwise long lists would
                overflow the viewport without surfacing a scrollbar. */}
            <div className="card endpoints-side" style={{padding: 0, position: 'sticky', top: 72, alignSelf: 'start', display:'flex', flexDirection:'column', height:'calc(100vh - 96px)', maxHeight:'calc(100vh - 96px)', overflow: 'hidden'}}>
              <div style={{padding:'10px 14px', borderBottom: '1px solid var(--line)', flexShrink: 0}}>
                <div className="row between" style={{alignItems:'center'}}>
                  <div className="micro">
                    Endpoints {loadingApi ? <Skel w={18} h={10} style={{marginLeft: 6}}/> : (
                      hideHiddenEps && isOwner
                        // Show "visible / total" so the owner knows the toggle is hiding rows
                        ? <>· {filteredEndpoints.length}<span style={{color:'var(--fg-4)'}}> / {api.endpoints.length}</span></>
                        : <>· {api.endpoints.length}</>
                    )}
                  </div>
                  {epQuery && <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)'}}>{filteredEndpoints.length} match</div>}
                </div>
                {/* Owner-only: quick switch to hide-the-hidden so creator can
                    preview their public surface inline. Counted hidden count
                    in label so they know what's being suppressed. */}
                {isOwner && api.endpoints.some(e => e.hidden) && (
                  <button
                    type="button"
                    onClick={() => setHideHiddenEps(v => !v)}
                    title={hideHiddenEps ? '点击恢复显示所有端点' : '点击只看公开端点（隐藏 hidden=true 的）'}
                    style={{
                      marginTop: 8, display: 'flex', alignItems: 'center', gap: 6,
                      width: '100%', padding: '4px 8px', height: 28, borderRadius: 5,
                      background: hideHiddenEps ? 'var(--bg-3)' : 'var(--bg-2)',
                      border: '1px solid var(--line)', cursor: 'pointer',
                      color: 'var(--fg-2)', fontSize: 11,
                    }}
                  >
                    {hideHiddenEps
                      ? <XIcons.IconEyeOff size={11} style={{color:'var(--fg-1)', flexShrink: 0}}/>
                      : <XIcons.IconEye    size={11} style={{color:'var(--fg-4)', flexShrink: 0}}/>}
                    <span style={{flex: 1, textAlign: 'left'}}>
                      {hideHiddenEps ? '只看公开端点' : '隐藏端点也显示'}
                    </span>
                    <span className="mono" style={{color:'var(--fg-4)', fontSize: 10}}>
                      {api.endpoints.filter(e => e.hidden).length} hidden
                    </span>
                  </button>
                )}
                {!loadingApi && api.endpoints.length > 8 && (
                  <div className="row gap-2" style={{
                    marginTop: 8, padding:'0 8px', height: 28, borderRadius: 5,
                    background:'var(--bg-2)', border:'1px solid var(--line)', alignItems:'center',
                  }}>
                    <XIcons.IconSearch size={11} style={{color:'var(--fg-4)', flexShrink: 0}}/>
                    <input value={epQuery} onChange={e => setEpQuery(e.target.value)}
                      placeholder="Filter endpoints…"
                      style={{flex: 1, height: '100%', minWidth: 0, background:'transparent', border:'none', outline:'none', fontSize: 11, color:'var(--fg-1)', fontFamily:'var(--font-mono)'}}/>
                    {epQuery && (
                      <button onClick={() => setEpQuery('')} className="btn btn-sm btn-ghost btn-icon"
                        style={{width: 18, height: 18, flexShrink: 0}} title="Clear">
                        <XIcons.IconX size={9}/>
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="col endpoints-side-list" style={{overflowY:'auto', overflowX:'hidden', flex: '1 1 0', minHeight: 0}}>
                {loadingApi ? (
                  // Render six skeleton rows so the sidebar has the right initial
                  // height while the real endpoint list streams in. Each row mirrors
                  // the actual endpoint button's two-line layout (method+name on top,
                  // path on bottom).
                  Array.from({length: 6}).map((_, i) => (
                    <div key={'sk' + i} style={{padding:'10px 14px', borderBottom:'1px solid var(--line)', flexShrink: 0}}>
                      <div style={{display:'flex', alignItems:'center', gap: 6, width:'100%'}}>
                        <Skel w={36} h={16} style={{borderRadius: 3, flexShrink: 0}}/>
                        <Skel w="60%" h={12} style={{flex: 1}}/>
                        <Skel w={28} h={14} style={{borderRadius: 3, flexShrink: 0}}/>
                      </div>
                      <div style={{marginTop: 6}}>
                        <Skel w="80%" h={10}/>
                      </div>
                    </div>
                  ))
                ) : filteredEndpoints.length === 0 ? (
                  <div className="micro-mono" style={{padding:'24px 14px', textAlign:'center', textTransform:'none', letterSpacing: 0, color:'var(--fg-4)'}}>
                    No endpoints match "{epQuery}"
                  </div>
                ) : (() => {
                  // 单个端点行(分组内/无分组都复用)。选中态交给 .ep-side-row/.is-selected
                  // (CSS 类)而不是内联样式 —— 这样背景/指示条才能 transition。
                  const renderEpRow = (ep) => (
                    <button key={ep.id} onClick={() => setSelEp(ep)}
                      className={`ep-side-row${selEp.id === ep.id ? ' is-selected' : ''}`}
                      title={ep.hidden
                        ? `${ep.name}\n${ep.method} ${ep.path}\n\n(已隐藏，仅创建者可见)`
                        : `${ep.name}\n${ep.method} ${ep.path}`}
                      style={{
                        textAlign:'left', padding:'10px 14px',
                        borderBottom:'1px solid var(--line)',
                        width: '100%', minWidth: 0, maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        flexShrink: 0,
                        opacity: ep.hidden ? 0.55 : 1,
                      }}>
                      <div style={{display:'flex', alignItems:'center', gap: 6, width:'100%', minWidth: 0}}>
                        <XPrim.MethodChip method={ep.method} />
                        <span style={{fontSize: 12, flex: 1, minWidth: 0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{ep.name}</span>
                        {ep.hidden && (
                          <XIcons.IconEyeOff size={11} style={{ color: 'var(--fg-4)', flexShrink: 0 }} />
                        )}
                        <BillingChip ep={ep} size="compact" />
                      </div>
                      <div className="row gap-2" style={{marginTop: 4, alignItems:'center', minWidth: 0}}>
                        <span className="mono" style={{fontSize: 11, color:'var(--fg-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex: 1, minWidth: 0}}>{ep.path}</span>
                        <AsyncTag mode={ep.asyncMode} />
                      </div>
                    </button>
                  );
                  // filteredEndpoints 已按 group 聚类 → 顺序切成有序分组
                  const groups = [];
                  const gmap = new Map();
                  for (const ep of filteredEndpoints) {
                    const key = (ep.group || '').trim() || '__ungrouped__';
                    if (!gmap.has(key)) { const o = { key, name: (ep.group || '').trim(), items: [] }; gmap.set(key, o); groups.push(o); }
                    gmap.get(key).items.push(ep);
                  }
                  const hasGroups = groups.some((g) => g.name);
                  // 没有任何分组 → 全平铺(不出现分组头)
                  if (!hasGroups) return filteredEndpoints.map(renderEpRow);
                  // 参考注册页:未分组端点平铺在顶部(不套文件夹),命名分组在下方作为可折叠文件夹
                  const ungrouped = groups.find((g) => !g.name);
                  const named = groups.filter((g) => g.name);
                  return (
                    <>
                      {ungrouped && ungrouped.items.map(renderEpRow)}
                      {named.map((grp) => {
                        const collapsed = !!collapsedGroups[grp.key];
                        return (
                          <React.Fragment key={grp.key}>
                            <div
                              onClick={() => toggleGroup(grp.key)}
                              className="ep-side-group"
                              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid var(--line)', flexShrink: 0 }}
                              title={collapsed ? '展开分组' : '收起分组'}
                            >
                              <XIcons.IconChevronDown size={13} className="ep-side-chevron" style={{ color: 'var(--fg-4)', flexShrink: 0, transform: collapsed ? 'rotate(-90deg)' : 'none' }} />
                              <XIcons.IconLayers size={14} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />
                              <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {grp.name}
                              </span>
                              <span className="num" style={{ flexShrink: 0, fontSize: 11, color: 'var(--fg-4)' }}>{grp.items.length}</span>
                            </div>
                            {!collapsed && grp.items.map(renderEpRow)}
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Try It body — keyed on the selected endpoint so switching
                endpoints replays the quick fade instead of a hard content cut. */}
            <div key={selEp.id} className="col gap-3 apx-fade" style={{minWidth: 0}}>
              {/* Endpoint header */}
              <div className="card" style={{padding:'14px 18px'}}>
                <div className="row between">
                  <div className="row gap-3" style={{minWidth: 0, flex: 1}}>
                    {loadingApi ? (
                      <>
                        <Skel w={48} h={20} style={{borderRadius: 4}}/>
                        <Skel w={220} h={16}/>
                      </>
                    ) : (
                      <>
                        <XPrim.MethodChip method={selEp.method} />
                        <span className="mono truncate" style={{fontSize: 14}}>{selEp.path}</span>
                      </>
                    )}
                  </div>
                  <div className="row gap-2" style={{fontSize: 12, color:'var(--fg-3)', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                    {loadingApi ? (
                      <Skel w={120} h={26} style={{borderRadius: 4}}/>
                    ) : (
                      <>
                        <BillingChip ep={selEp} size="lg" />
                        <AsyncTag mode={selEp.asyncMode} size="lg" />
                        {selEp.supportsStreaming && (
                          <span className="mono" style={{padding:'6px 10px', fontSize: 11, color:'var(--ok)', background:'rgba(46,204,113,0.08)', border:'1px solid rgba(46,204,113,0.25)', borderRadius: 4, display:'inline-flex', alignItems:'center', gap: 6}}>
                            <span style={{width: 5, height: 5, borderRadius:'50%', background:'var(--ok)'}}/>
                            streaming
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {loadingApi ? (
                  <div style={{marginTop: 10}}>
                    <Skel w="100%" h={13} style={{display:'block', marginBottom: 5}}/>
                    <Skel w="60%" h={13} style={{display:'block'}}/>
                  </div>
                ) : (
                  <p style={{margin:'10px 0 0', fontSize: 13, color:'var(--fg-2)'}}>{selEp.desc || <span style={{color:'var(--fg-4)'}}>No description.</span>}</p>
                )}
                <ModelChips models={selEp.servedModels} limit={8} />
                <BillingDetailsPanel ep={selEp} />
              </div>

              {(isFreeEndpoint(selEp) || isFreeApiAccountError(resp)) && (
                <FreeApiAccessNotice
                  authed={_authed} user={_authUser} hasKey={hasKey}
                  rejected={isFreeApiAccountError(resp)} navigate={navigate}
                />
              )}

              {/* WEBSOCKET endpoints render the WS Try-It view (档1 console / 档2 playground)
                  instead of the HTTP request/response fetch cards. */}
              {isWs ? (
                // key=端点 id：切换端点时强制整块重挂载，否则 tab/console/playground 会带着
                // 上一个端点的旧状态（连接、帧、相位），三个端点看起来"共用一套模板"。
                <WsTryItPanel key={selEp._raw?.id || selEp.id} api={api} endpoint={selEp} adapters={wsAdapters} />
              ) : (
              <>
              {/* Request (full-width) */}
              <div className="card">
                  <div className="row between" style={{padding:'10px 14px', borderBottom:'1px solid var(--line)'}}>
                    <div className="micro">Request</div>
                    <div className="row gap-2">
                      <select className="hairline" value={lang} onChange={e=>setLang(e.target.value)}
                        style={{height: 24, padding:'0 6px', fontSize: 11, background:'var(--bg-2)', color:'var(--fg-2)', border:'1px solid var(--line-2)', borderRadius: 3, outline:'none'}}>
                        <option value="curl">cURL</option>
                        <option value="js">JavaScript</option>
                        <option value="python">Python</option>
                      </select>
                      <XPrim.CopyBtn text={codes[lang]} />
                    </div>
                  </div>

                  <div className="api-req-body" style={{padding: 14, display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap: 16}}>
                    {/* Left: inputs */}
                    <div style={{minWidth: 0}}>
                      <div className="row between" style={{marginBottom: 10, alignItems:'flex-start'}}>
                        <div className="field-label mono" style={{marginBottom: 0, paddingTop: 8}}>AUTH</div>
                        <div>
                          {hasKey ? (
                            <button className="btn btn-sm btn-ghost" onClick={()=>navigate('console', {tab:'env'})} title="Manage in Environment">
                              <XIcons.IconCheck size={11} style={{color:'var(--ok)'}}/>
                              <span className="mono" style={{fontSize: 11}}>XAPI_KEY</span>
                              <span className="mono" style={{fontSize: 11, color:'var(--fg-3)'}}>{keyForDisplay}</span>
                            </button>
                          ) : (
                            <button className="btn btn-sm" onClick={()=>navigate('console', {tab:'env', focus:'XAPI_KEY'})} style={{borderColor:'var(--warn)', color:'var(--warn)'}}>
                              <span style={{width: 6, height: 6, borderRadius:'50%', background:'var(--warn)'}}/>
                              <span style={{fontSize: 11}}>Set XAPI_KEY</span>
                              <XIcons.IconArrowRight size={11}/>
                            </button>
                          )}
                        </div>
                      </div>

                      {pathParamNames.length > 0 && (
                        <>
                          <div className="field-label mono" style={{marginTop: 4}}>PATH PARAMS</div>
                          <div className="hairline" style={{borderRadius: 5, overflow:'hidden', background:'var(--bg-2)'}}>
                            {pathParamNames.map((n, i) => {
                              const def = selEp?._raw?.pathParams?.[n];
                              const required = !!(def && def.required);
                              const tooltip = (def && def.description) || (def && def.schema && def.schema.type ? `type: ${def.schema.type}` : '');
                              return (
                                <div key={n} className="row gap-2" style={{padding:'6px 10px', borderBottom: i < pathParamNames.length-1 ? '1px solid var(--line)' : 'none'}} title={tooltip}>
                                  <span className="mono" style={{fontSize: 11, color:'var(--fg-3)', minWidth: 100}}>:{n}{required && <span style={{color:'var(--err)', marginLeft: 2}}>*</span>}</span>
                                  <input className="input mono" style={{flex:1, height: 24, fontSize: 11, background:'transparent', border:'none', padding: 0}}
                                    placeholder={(def && def.example != null ? String(def.example) : n)} value={pathParams[n] || ''} onChange={e=>setPathParams({...pathParams, [n]: e.target.value})}/>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}

                      <div className="row between" style={{marginTop: pathParamNames.length ? 14 : 4, alignItems:'center'}}>
                        <div className="field-label mono" style={{marginBottom: 0}}>QUERY PARAMS</div>
                        <button className="btn btn-sm btn-ghost btn-icon" onClick={addQuery} title="Add query param"><XIcons.IconPlus size={11}/></button>
                      </div>
                      <div className="hairline" style={{borderRadius: 5, overflow:'hidden', background:'var(--bg-2)'}}>
                        {queryParams.length === 0 && <div className="micro-mono" style={{padding:'8px 10px', textTransform:'none', letterSpacing: 0}}>No query params — click + to add.</div>}
                        {queryParams.map((p, i) => {
                          const def = selEp?._raw?.params?.[p.k];
                          const required = !!(def && def.required);
                          const tooltip = (def && def.description) || (def && def.schema && def.schema.type ? `type: ${def.schema.type}` : '');
                          return (
                            <div key={i} className="row gap-2" style={{padding:'4px 6px 4px 10px', borderBottom: i < queryParams.length-1 ? '1px solid var(--line)' : 'none'}} title={tooltip}>
                              <div style={{position:'relative', width: 120, display:'flex', alignItems:'center'}}>
                                {p.locked ? (
                                  <span className="mono truncate" style={{width:'100%', fontSize: 11, color:'var(--fg-3)'}}>{p.k}</span>
                                ) : (
                                  <input className="input mono" style={{width: '100%', height: 24, fontSize: 11, background:'transparent', border:'none', padding: 0}}
                                    placeholder="key" value={p.k} onChange={e=>{ const x=[...queryParams]; x[i]={...x[i], k: e.target.value}; setQueryParams(x); }}/>
                                )}
                                {required && <span style={{position:'absolute', right: 4, color:'var(--err)', fontSize: 11, pointerEvents:'none'}}>*</span>}
                              </div>
                              <input className="input mono" style={{flex:1, height: 24, fontSize: 11, background:'transparent', border:'none', padding: 0}}
                                placeholder={def && def.example != null ? String(def.example) : 'value (use ${VAR} for env)'} value={p.v} onChange={e=>{ const x=[...queryParams]; x[i]={...x[i], v: e.target.value}; setQueryParams(x); }}/>
                              {p.locked
                                ? <span style={{width: 22, height: 22}} />
                                : <button className="btn btn-sm btn-ghost btn-icon" style={{width: 22, height: 22}} onClick={()=>setQueryParams(qs => qs.filter((_,j) => j !== i))}><XIcons.IconX size={10}/></button>}
                            </div>
                          );
                        })}
                      </div>

                      <div className="row between" style={{marginTop: 14, alignItems:'center'}}>
                        <div className="field-label mono" style={{marginBottom: 0}}>HEADERS</div>
                        <button className="btn btn-sm btn-ghost btn-icon" onClick={addHeader} title="Add header"><XIcons.IconPlus size={11}/></button>
                      </div>
                      <div className="hairline" style={{borderRadius: 5, overflow:'hidden', background:'var(--bg-2)'}}>
                        <div className="row gap-2" style={{padding:'6px 10px', borderBottom:'1px solid var(--line)'}}>
                          <span className="mono" style={{fontSize: 11, color:'var(--fg-3)', width: 120, flexShrink: 0}}>xapi-key</span>
                          <span className="mono truncate" style={{fontSize: 11, color: hasKey ? 'var(--fg-1)' : 'var(--warn)', flex:1}}>{keyForDisplay}</span>
                        </div>
                        <div className="row gap-2" style={{padding:'6px 10px', borderBottom: extraHeaders.length ? '1px solid var(--line)' : 'none'}}>
                          <span className="mono" style={{fontSize: 11, color:'var(--fg-3)', width: 120, flexShrink: 0}}>Content-Type</span>
                          <span className="mono truncate" style={{fontSize: 11, color: 'var(--fg-1)', flex:1}}>application/json</span>
                        </div>
                        {extraHeaders.map((p, i) => {
                          const def = selEp?._raw?.headers?.[p.k];
                          const required = !!(def && def.required);
                          const tooltip = (def && def.description) || (def && def.schema && def.schema.type ? `type: ${def.schema.type}` : '');
                          return (
                            <div key={i} className="row gap-2" style={{padding:'4px 6px 4px 10px', borderBottom: i < extraHeaders.length-1 ? '1px solid var(--line)' : 'none'}} title={tooltip}>
                              <div style={{position:'relative', width: 120, display:'flex', alignItems:'center'}}>
                                {p.locked ? (
                                  <span className="mono truncate" style={{width:'100%', fontSize: 11, color:'var(--fg-3)'}}>{p.k}</span>
                                ) : (
                                  <input className="input mono" style={{width: '100%', height: 24, fontSize: 11, background:'transparent', border:'none', padding: 0}}
                                    placeholder="header" value={p.k} onChange={e=>{ const x=[...extraHeaders]; x[i]={...x[i], k: e.target.value}; setExtraHeaders(x); }}/>
                                )}
                                {required && <span style={{position:'absolute', right: 4, color:'var(--err)', fontSize: 11, pointerEvents:'none'}}>*</span>}
                              </div>
                              <input className="input mono" style={{flex:1, height: 24, fontSize: 11, background:'transparent', border:'none', padding: 0}}
                                placeholder={def && def.example != null ? String(def.example) : 'value (use ${VAR} for env)'} value={p.v} onChange={e=>{ const x=[...extraHeaders]; x[i]={...x[i], v: e.target.value}; setExtraHeaders(x); }}/>
                              {p.locked
                                ? <span style={{width: 22, height: 22}} />
                                : <button className="btn btn-sm btn-ghost btn-icon" style={{width: 22, height: 22}} onClick={()=>setExtraHeaders(hs => hs.filter((_,j) => j !== i))}><XIcons.IconX size={10}/></button>}
                            </div>
                          );
                        })}
                      </div>

                      {selEp.method !== 'GET' && (
                        isFormBody ? (
                          <>
                            <div className="field-label mono" style={{marginTop: 14}}>BODY · {bodyCt}</div>
                            <div className="col gap-2">
                              {formFields.length === 0 && (
                                <div style={{fontSize:12, color:'var(--fg-3)'}}>No fields.</div>
                              )}
                              {formFields.map((f, i) => (
                                <div key={i} className="row gap-2" style={{alignItems:'center'}}>
                                  <span className="mono" style={{flex:'0 0 150px', fontSize:12, color:'var(--fg-2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                                    {f.key}{f.type === 'file' ? ' · file' : ''}
                                  </span>
                                  {f.type === 'file' ? (
                                    <input type="file" style={{flex:1, fontSize:12}}
                                      onChange={e=>{ const x=[...formFields]; x[i]={...x[i], file:(e.target.files && e.target.files[0]) || null}; setFormFields(x); }} />
                                  ) : (
                                    <input className="input mono" style={{flex:1}} placeholder="value (use ${VAR} for env)" value={f.value}
                                      onChange={e=>{ const x=[...formFields]; x[i]={...x[i], value:e.target.value}; setFormFields(x); }} />
                                  )}
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="field-label mono" style={{marginTop: 14}}>BODY · application/json</div>
                            <textarea className="input" value={reqBody} onChange={e=>setReqBody(e.target.value)} rows={8} />
                          </>
                        )
                      )}

                      {asyncMode !== 'NONE' && (
                        <div style={{marginTop: 14, padding: 12, borderRadius: 6, background: 'var(--bg-2)', border: '1px solid var(--line)'}}>
                          {asyncMode === 'OPTIONAL' ? (
                            <div className="row between" style={{alignItems:'center', gap: 12}}>
                              <div style={{minWidth: 0}}>
                                <div className="row gap-2" style={{alignItems:'center'}}>
                                  <XIcons.IconZap size={12} style={{color:'var(--c-mint)'}}/>
                                  <span style={{fontSize: 13, fontWeight: 500}}>{tDetail('apidetail.tryit.async.toggle')}</span>
                                </div>
                                <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, marginTop: 4, color:'var(--fg-3)'}}>
                                  {tDetail('apidetail.tryit.async.toggleHint')}
                                </div>
                              </div>
                              <button type="button" onClick={() => setAsyncOptIn(v => !v)}
                                className={`btn btn-sm ${asyncOptIn ? 'btn-primary' : ''}`}
                                style={{minWidth: 56, justifyContent:'center'}}>
                                {asyncOptIn ? 'On' : 'Off'}
                              </button>
                            </div>
                          ) : (
                            <div className="row gap-2" style={{alignItems:'center', color:'var(--c-mint)', fontSize: 12}}>
                              <XIcons.IconZap size={12}/>
                              <span>{tDetail('apidetail.tryit.async.required')}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)'}}>
                        {loading ? (
                          <button className="btn" onClick={abort} style={{width:'100%', justifyContent:'center', height: 38, borderColor:'var(--err)', color:'var(--err)'}}>
                            <XIcons.IconPause size={12}/> <span>Abort request</span>
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary"
                            onClick={send}
                            disabled={!hasKey}
                            style={{width:'100%', justifyContent:'center', height: 38, opacity: !hasKey ? 0.6 : 1}}
                            title={!hasKey ? 'Set XAPI_KEY to send' : ''}
                          >
                            {!hasKey ? <><XIcons.IconKey size={12}/> <span>Set XAPI_KEY to send</span></>
                              : useAsync
                                ? <><XIcons.IconZap size={12}/> <span>Submit async task</span></>
                                : <><XIcons.IconPlay size={12}/> <span>Send request</span></>}
                          </button>
                        )}
                        <div className="row between" style={{marginTop: 10, color:'var(--fg-4)', fontSize: 11, gap: 12, alignItems:'flex-start'}}>
                          <span className="mono" style={{color:'var(--fg-3)', minWidth: 0, flex: 1, overflowWrap:'anywhere', wordBreak:'break-all', lineHeight: 1.5}}>{selEp.method} {fullPath}</span>
                          <span className="mono" style={{flexShrink: 0, whiteSpace:'nowrap'}}>{endpointBillingRuntimeText(selEp)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right: generated code */}
                    <div style={{minWidth: 0}}>
                      <div className="field-label mono">CODE · {lang}</div>
                      <pre className="code" style={{margin: 0, maxHeight: 360, fontSize: 11.5}}>{codes[lang]}</pre>
                    </div>
                  </div>
                </div>

                {/* Response (full-width below request) */}
                <div className="card">
                  <div className="row between" style={{padding:'10px 14px', borderBottom:'1px solid var(--line)'}}>
                    <div className="micro">Response</div>
                    {resp && !asyncTask && (
                      <div className="row gap-2">
                        <span className="tag" style={{color: resp.status === 200 ? 'var(--ok)' : 'var(--err)'}}>
                          <span className="tag-dot ok" style={{background: resp.status === 200 ? 'var(--ok)' : 'var(--err)'}}/>
                          {resp.status}
                        </span>
                        <span className="tag mono">{resp.latency}ms</span>
                      </div>
                    )}
                    {asyncTask && (
                      <div className="row gap-2">
                        {(asyncTask.status === 'pending' || asyncTask.status === 'processing') ? (
                          <span className="tag" style={{
                            color: 'var(--c-amber)',
                            background: 'var(--c-amber-bg)',
                            borderColor: 'color-mix(in oklab, var(--c-amber) 40%, transparent)',
                            display:'inline-flex', alignItems:'center', gap: 6,
                          }}>
                            <span style={{
                              width: 7, height: 7, borderRadius: '50%',
                              background: 'var(--c-amber)',
                              boxShadow: '0 0 8px var(--c-amber)',
                              animation: 'pulse-dot 1.2s ease-in-out infinite',
                            }}/>
                            {tDetail(`console.tasks.status.${asyncTask.status}`)}
                          </span>
                        ) : (
                          <span className="tag mono" style={{
                            color: asyncTask.status === 'succeeded' ? 'var(--ok)' : 'var(--err)',
                          }}>
                            {tDetail(`console.tasks.status.${asyncTask.status}`)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {asyncTask && (() => {
                    const isPolling = asyncTask.status === 'pending' || asyncTask.status === 'processing';
                    const elapsedMs = asyncTask.submittedAt ? Date.now() - asyncTask.submittedAt : 0;
                    void asyncTick; // re-read elapsed when the per-second tick fires
                    const elapsedStr = (() => {
                      const s = Math.max(0, Math.floor(elapsedMs / 1000));
                      if (s < 60) return `${s}s`;
                      const m = Math.floor(s / 60);
                      const rs = s % 60;
                      return `${m}m ${rs}s`;
                    })();
                    return (
                      <div style={{
                        padding: 14, borderBottom:'1px solid var(--line)',
                        ...(isPolling ? {
                          background: 'color-mix(in oklab, var(--c-amber) 5%, transparent)',
                          borderLeft: '3px solid var(--c-amber)',
                        } : {}),
                      }}>
                        <div className="row between" style={{marginBottom: 10, gap: 12, flexWrap:'wrap'}}>
                          <div style={{minWidth: 0}}>
                            <div className="micro" style={{marginBottom: 4}}>{tDetail('apidetail.tryit.async.submitted')}</div>
                            <div className="mono" style={{fontSize: 12, color:'var(--fg-2)', wordBreak:'break-all'}}>{asyncTask.taskId}</div>
                          </div>
                          <div className="row gap-2" style={{alignItems:'center', flexShrink: 0}}>
                            {isPolling && (
                              <span className="row gap-2" style={{
                                alignItems:'center', fontSize: 13, fontWeight: 500,
                                color:'var(--c-amber)',
                                padding: '6px 10px',
                                background: 'var(--c-amber-bg)',
                                border: '1px solid color-mix(in oklab, var(--c-amber) 30%, transparent)',
                                borderRadius: 6,
                              }}>
                                <div className="spinner" style={{width: 14, height: 14, borderColor:'color-mix(in oklab, var(--c-amber) 30%, transparent)', borderTopColor:'var(--c-amber)'}}/>
                                {tDetail('apidetail.tryit.async.polling')}
                                <span className="mono" style={{opacity: 0.85, fontVariantNumeric: 'tabular-nums'}}>· {elapsedStr}</span>
                              </span>
                            )}
                            <button className="btn btn-sm" onClick={() => navigate('console', { tab: 'tasks', taskId: asyncTask.taskId })}>
                              <XIcons.IconArrowRight size={11}/> {tDetail('apidetail.tryit.async.viewInConsole')}
                            </button>
                          </div>
                        </div>
                        {isPolling && asyncTask.expiresAt && (
                          <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)'}}>
                            {tDetail('apidetail.tryit.async.expiresIn')} {(() => {
                              const ms = new Date(asyncTask.expiresAt).getTime() - Date.now();
                              if (ms <= 0) return 'expired';
                              const m = Math.floor(ms / 60000);
                              return m >= 60 ? `${(m / 60).toFixed(1)}h` : `${m}m`;
                            })()}
                          </div>
                        )}
                        {/* Long-job hint: after 30s of polling with no result, surface a
                            line so users don't think the page is stuck. Common for
                            UPSTREAM_POLLING video gen (60-120s typical). */}
                        {isPolling && elapsedMs >= 30_000 && (
                          <div className="row gap-2" style={{
                            marginTop: 10, padding: '8px 10px',
                            background: 'var(--bg-2)', border: '1px solid var(--line)',
                            borderRadius: 6, fontSize: 12, color: 'var(--fg-3)',
                          }}>
                            <XIcons.IconInfo size={12} />
                            <span>{tDetail('apidetail.tryit.async.longJobHint')}</span>
                          </div>
                        )}
                        {(asyncTask.status === 'succeeded' || asyncTask.status === 'failed' || asyncTask.status === 'expired') && (
                          <div style={{marginTop: 10}}>
                            <TaskResultView
                              result={asyncTask.result}
                              error={asyncTask.error}
                              status={asyncTask.status}
                              onOpenJson={(v) => setJsonModal({ title: 'Task result', value: v })}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{padding: 14, minHeight: 220}}>
                    {!resp && !loading && (() => {
                      // If the provider configured an example response, show it as a preview
                      // instead of the bare placeholder. Picks the first 2xx response.
                      const examples = Array.isArray(selEp?._raw?.responses) ? selEp._raw.responses : [];
                      const sample = examples.find(r => Number(r.status_code) >= 200 && Number(r.status_code) < 300) || examples[0];
                      if (sample && sample.body != null) {
                        const bodyText = typeof sample.body === 'string' ? sample.body : JSON.stringify(sample.body, null, 2);
                        return (
                          <div>
                            <div className="row between" style={{marginBottom: 8}}>
                              <span className="micro-mono" style={{color:'var(--fg-3)'}}>Example response · {sample.status_code || 200}{sample.label ? ` · ${sample.label}` : ''}</span>
                              <span className="micro-mono" style={{color:'var(--fg-4)'}}>Send to call upstream</span>
                            </div>
                            <pre className="code" style={{maxHeight: 280, margin: 0, whiteSpace:'pre-wrap', opacity: 0.75}}>{bodyText}</pre>
                          </div>
                        );
                      }
                      return (
                        <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight: 180, color:'var(--fg-4)', fontSize: 13, textAlign:'center', padding: '20px 16px'}}>
                          <div className="striped hairline" style={{width: 48, height: 48, borderRadius: 8, marginBottom: 16, display:'flex', alignItems:'center', justifyContent:'center'}}>
                            <XIcons.IconPlay size={16} />
                          </div>
                          <div style={{lineHeight: 1.6}}>Click <span className="mono" style={{color:'var(--fg-2)'}}>Send request</span> to execute</div>
                          <div style={{marginTop: 6, fontSize: 11, lineHeight: 1.5}}>Cost: <span className="mono">{endpointBillingRuntimeText(selEp)}</span></div>
                        </div>
                      );
                    })()}
                    {loading && (
                      <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height: 180, color:'var(--fg-3)'}}>
                        <div className="spinner" style={{width: 22, height: 22}}/>
                        <div style={{marginTop: 12, fontSize: 13}}>Calling {api.host}…</div>
                      </div>
                    )}
                    {resp && (() => {
                      const bodyValue = resp.streaming ? (streamText || resp.body || '(empty stream)') : resp.body;
                      // 二进制响应(audio/image/video/…):invoke() 已用 res.blob() 接收,这里用
                      // body instanceof Blob 分流到 TaskFileRenderer(内联播放/显示/下载),
                      // 不再当文本渲染。content-type 优先取响应头,兜底用 blob.type。
                      const isBlob = typeof Blob !== 'undefined' && bodyValue instanceof Blob;
                      const blobCt = (resp.headers && (resp.headers['content-type'] || resp.headers['Content-Type']))
                        || (isBlob ? bodyValue.type : '') || '';
                      // JSON.stringify(undefined) returns undefined (not a string), and `bodyText.length`
                      // below would crash. Coerce to '' so the network-error case (resp.body absent) is safe.
                      const bodyText = isBlob ? '' : (typeof bodyValue === 'string'
                        ? bodyValue
                        : (bodyValue == null ? '' : (JSON.stringify(bodyValue, null, 2) ?? '')));
                      return (
                        // apx-fade plays once when a fresh response mounts
                        // (send() resets resp to null first) — no replay while
                        // streaming chunks accumulate into the same response.
                        <div className="api-resp-grid apx-fade" style={{display:'grid', gridTemplateColumns:'260px minmax(0, 1fr)', gap: 16}}>
                          <div>
                            <div className="row between" style={{marginBottom: 6}}>
                              <span className="field-label mono" style={{margin: 0}}>HEADERS</span>
                              {resp.headers && Object.keys(resp.headers).length > 0 && (
                                <button className="btn btn-sm btn-ghost btn-icon" title="Expand"
                                  onClick={() => setJsonModal({ title: 'Response headers', value: resp.headers })}>
                                  <XIcons.IconMaximize size={11}/>
                                </button>
                              )}
                            </div>
                            <div className="hairline mono" style={{borderRadius: 5, overflow:'hidden', background:'var(--bg-2)', padding:'6px 10px', fontSize: 11, color:'var(--fg-2)', lineHeight: 1.7, maxHeight: 280, overflowY:'auto'}}>
                              {resp.headers && Object.keys(resp.headers).length > 0
                                ? Object.entries(resp.headers).map(([k,v]) => (
                                    <div key={k}><span style={{color:'var(--fg-3)'}}>{k}:</span> {String(v)}</div>
                                  ))
                                : <span style={{color:'var(--fg-4)'}}>—</span>}
                              {resp.error && <div style={{color:'var(--err)', marginTop: 6}}>error: {resp.error}</div>}
                            </div>
                          </div>
                          <div style={{minWidth: 0}}>
                            <div className="row between" style={{marginBottom: 6}}>
                              <span className="field-label mono" style={{margin: 0}}>BODY {resp.streaming ? '· streaming' : ''} {!isBlob && <span style={{color:'var(--fg-4)', textTransform:'none'}}>· {bodyText.length.toLocaleString()} chars</span>}</span>
                              {/* Blob body 不能复制文本/JSON-Expand;下载由 TaskFileRenderer 自带的 Download 提供。 */}
                              {!isBlob && (
                                <div className="row gap-1">
                                  <XPrim.CopyBtn text={bodyText} />
                                  <button className="btn btn-sm btn-ghost" title="Expand to full screen"
                                    onClick={() => setJsonModal({ title: 'Response body', value: bodyValue })}>
                                    <XIcons.IconMaximize size={11}/> Expand
                                  </button>
                                </div>
                              )}
                            </div>
                            {isBlob ? (
                              <TaskFileRenderer url={respBlobUrl} mime={blobCt} bytes={bodyValue.size} expired={false} />
                            ) : (
                              <>
                                <B64ImageStrip images={extractB64Images(bodyValue)}/>
                                <XPrim.JsonViewer value={bodyValue} maxHeight={280} />
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </>
              )}
            </div>
          </div>

          {/* Mobile endpoint picker drawer */}
          {epPickerOpen && typeof document !== 'undefined' && createPortal(
            <>
              <div className="mobile-sheet-backdrop" onClick={()=>setEpPickerOpen(false)} />
              <div className="market-filter-sheet" role="dialog" aria-label="Endpoints" onClick={(e)=>e.stopPropagation()}>
                <div className="row between" style={{ alignItems:'center', marginBottom: 14 }}>
                  <div className="micro">Endpoints · {api.endpoints.length}</div>
                  <button onClick={()=>setEpPickerOpen(false)} className="btn btn-sm btn-ghost btn-icon" aria-label="Close">
                    <XIcons.IconX size={12}/>
                  </button>
                </div>
                {api.endpoints.length > 8 && (
                  <div className="row gap-2" style={{
                    marginBottom: 12, padding:'0 10px', height: 36, borderRadius: 6,
                    background:'var(--bg-2)', border:'1px solid var(--line)', alignItems:'center',
                  }}>
                    <XIcons.IconSearch size={12} style={{color:'var(--fg-4)', flexShrink: 0}}/>
                    <input value={epQuery} onChange={e => setEpQuery(e.target.value)}
                      placeholder="Filter endpoints…"
                      style={{flex: 1, height: '100%', minWidth: 0, background:'transparent', border:'none', outline:'none', fontSize: 13, color:'var(--fg-1)', fontFamily:'var(--font-mono)'}}
                      autoFocus/>
                  </div>
                )}
                <div className="col gap-1">
                  {filteredEndpoints.map(ep => (
                    <button key={ep.id} onClick={()=>{ setSelEp(ep); setEpPickerOpen(false); }}
                      className={`ep-side-row${selEp.id === ep.id ? ' is-selected' : ''}`}
                      title={ep.hidden ? '已隐藏，仅创建者可见' : undefined}
                      style={{
                        textAlign:'left', padding:'12px 14px', borderRadius: 6,
                        border:'1px solid var(--line)', width:'100%',
                        // Same hidden treatment as the desktop sidebar
                        opacity: ep.hidden ? 0.55 : 1,
                      }}>
                      <div className="row gap-2" style={{alignItems:'center', marginBottom: 4}}>
                        <XPrim.MethodChip method={ep.method} />
                        <span style={{fontSize: 13, flex: 1, minWidth: 0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{ep.name}</span>
                        {ep.hidden && (
                          <XIcons.IconEyeOff size={11} style={{ color: 'var(--fg-4)', flexShrink: 0 }} />
                        )}
                        <BillingChip ep={ep} size="compact" />
                      </div>
                      <div className="mono" style={{fontSize: 11.5, color:'var(--fg-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{ep.path}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>,
            document.body,
          )}
          </>
        )}

        {tab === 'endpoints' && (
          <>
            <div className="card endpoints-table-wrap">
              <table className="table">
                <thead><tr><th>Method</th><th>Path</th><th>Name</th><th>Billing</th><th>Description</th></tr></thead>
                <tbody>
                  {api.endpoints.map(ep => (
                    <tr key={ep.id}>
                      <td><XPrim.MethodChip method={ep.method} /></td>
                      <td className="mono" style={{fontSize: 12}}>{ep.path}</td>
                      <td>{ep.name}</td>
                      <td><BillingChip ep={ep} /></td>
                      <td style={{color:'var(--fg-3)', fontSize: 13}}>
                        {ep.desc}
                        <ModelChips models={ep.servedModels} limit={4} compact />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="endpoints-mobile">
              {api.endpoints.map(ep => (
                <div key={ep.id} className="ep-card">
                  <div className="row gap-2" style={{alignItems:'center', minWidth: 0}}>
                    <XPrim.MethodChip method={ep.method} />
                    <span className="mono" style={{fontSize: 12, color:'var(--fg-2)', flex: 1, minWidth: 0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{ep.path}</span>
                  </div>
                  <div style={{marginTop: 8, fontSize: 13, color:'var(--fg-1)'}}>{ep.name}</div>
                  {ep.desc && <div style={{marginTop: 4, fontSize: 12, color:'var(--fg-3)', lineHeight: 1.55}}>{ep.desc}</div>}
                  <ModelChips models={ep.servedModels} limit={5} compact />
                  <div style={{marginTop: 10}}><BillingChip ep={ep} /></div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'docs' && <DocsTab api={api} gwUrl={gwUrl} keyForDisplay={keyForDisplay} hasKey={hasKey} />}

        {tab === 'about' && (
          api.aboutMarkdown ? (
            <div
              className="skills-md api-about-md"
              dangerouslySetInnerHTML={{ __html: renderSkillMarkdown(api.aboutMarkdown) }}
            />
          ) : (
            <div className="apx-empty">
              <div className="apx-empty-icon"><XIcons.IconBook size={16} /></div>
              <div className="apx-empty-title">No About document yet</div>
              <div className="apx-empty-body">
                {api.description || 'The provider hasn’t published an About document for this API.'}
              </div>
              <div className="apx-empty-hint">
                Providers can write Markdown in Workspace → 基本信息 → About 文档
              </div>
            </div>
          )
        )}

        {tab === 'skill' && (
          api.linkedSkill?.slug ? (
            <SkillTabContent linkedSkill={api.linkedSkill} navigate={navigate} />
          ) : (
            <div className="apx-empty">
              <div className="apx-empty-icon"><XIcons.IconTerminal size={16} /></div>
              <div className="apx-empty-title">No Agent Skill linked</div>
              <div className="apx-empty-body">
                No published Agent Skill is linked to this API yet.
              </div>
              <div className="apx-empty-hint">
                Providers can configure one in the workspace Skill section.
              </div>
            </div>
          )
        )}

        {tab === 'changelog' && <ChangelogTab api={api} />}
       </div>
      </div>

      <XPrim.JsonModal
        open={!!jsonModal}
        onClose={() => setJsonModal(null)}
        title={jsonModal?.title}
        value={jsonModal?.value}
      />

      <HealthDialog
        open={healthOpen}
        onClose={() => setHealthOpen(false)}
        data={healthData}
        onRefresh={refreshHealth}
        apiServiceId={route.params.id}
        t={tDetail}
      />
    </div>
  );
};

export const XApiDetail = { ApiDetailPage };
export {
  ApiDetailPage,
  BillingChip,
  BillingDetailsPanel,
  WsPricingPanel,
  headerBillingLabel,
  wsChipInfo,
};
if (typeof window !== 'undefined') window.XApiDetail = XApiDetail;
